// src/pages/1_DD.js
import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { SearchIcon, DocumentTextIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, XCircleIcon, XIcon, ExclamationIcon, InformationCircleIcon } from '@heroicons/react/solid';
// import { SearchIcon, DocumentTextIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, XCircleIcon, StopIcon, MicrophoneIcon, XIcon } from '@heroicons/react/solid';
import Breadcrumb from '../../../components/Breadcrumb';
import { Tooltip } from 'react-tooltip';
import InfoTooltip from '../../../components/InfoTooltip';

import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { ProformaNavigationGuardContext } from '../../../context/ProformaNavigationGuardContext';
import * as Dialog from '@radix-ui/react-dialog';
import { auth } from '../../../firebase';
import { serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { DocumentEmbedDialog } from '../8_diagnosticDecision/components/DocumentEmbedDialog';
import { useEmbeddedReportUrl } from '../../../hooks/useEmbeddedReportUrl';
import { formatDateTime, isValidTimestamp } from '../../../utils/timestampUtils';
// import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import ModuleCompletionDialog, { CancelButton, PrimaryActionButton, GenerateReportButton, SummarySection, DialogMessage } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import NonADHDDiagnosticConfirmation from '../8_diagnosticDecision/components/NonADHDDiagnosticConfirmation';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import { isADHDProvisionalPrimaryDiagnosis } from '../../../utils/conditionChecks';
import {
  isClinicianAssignedToStage,
  isLeadClinician,
  isSSILeadOverrideActive,
} from '../../../utils/clinicianPermissions';
import { buildFirstGeneratedFields, buildFirstCompletedFields, buildFirstCompletedFieldsDotNotation, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import {
  hasActionableOtherConditions,
  buildDDNavSections,
  getDDRouteForSectionIndex,
  getFlatOtherConditionsStepTitle,
  isAdhdPositiveDecision,
} from './cypDdRouting';
import { isSupportingDocStale } from '../../../utils/supportingDocsVisibility';
import DDSideNavbar from '../8_diagnosticDecision/components/DDSideNavbar';
// SSI Conflict Detection for 3 Borough / SLaM workflow (legacy RDD path)
import { detectSSIConflict } from '../../../utils/ssiConflictDetection';
import SSIConflictAlert from '../../../components/SSIConflictAlert';
import { TriggerResetSSI } from '../../../utils/TriggerResetSSI';
import { TriggerSembleMdtReport } from '../../../utils/TriggerSembleMdtReport';
// Lead SSI consistency dialog (SLaM / 3 Borough stamped leadOverride path)
import { computeCriterionRefresh, isUnresolvedSSIDdMismatch, shouldProceedAfterTakeover } from '../../../utils/ssiDdConsistency';
import SSIDdConsistencyDialog from '../../../components/SSIDdConsistencyDialog';
import { shouldShowPadDdMismatch } from '../../../components/PadDdMismatchDialog';
import { TriggerOverrideSSI } from '../../../utils/TriggerOverrideSSI';
import { toast } from 'react-toastify';
import { resolvePadStatusOnDdComplete } from '../../../utils/padUnlockUtils';

/**
 * One SSI decision cell on the summary tables: a tick, a cross, the CYP SSI's
 * "Inconclusive" (named, never coloured as present or absent) or Not recorded.
 */
function SsiDecisionCell({ value }) {
  if (value === true) return <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />;
  if (value === false) return <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />;
  if (value === 'inconclusive') return <span className="text-amber-800 text-xs font-semibold">Inconclusive</span>;
  return <span className="text-gray-400 text-xs">Not recorded</span>;
}

// Supporting evidence is one tick-list for both Fully met and Partially met,
// same control as adult ADHD (one list, so a ticked source survives a switch
// between the two). "None available" cannot be combined with any other
// source. Not met hides the list.
// Ordering: direct clinical observation first; "None available" last.
// Slash convention: no spaces around "/" (e.g. "Parent/carer" not "Parent / carer").
// Exported for test assertions on list shape.
export const CYP_SUPPORTING_EVIDENCE = [
  'Direct clinical observation',
  'Parent/carer report',
  'Teacher/SENCO report',
  'Educational assessment reports',
  'Sibling/peer reports',
  'Youth worker/social care reports',
  'CAMHS reports',
  'Youth justice reports',
  'EHCP/SEN documentation',
  'School records',
  'Medical records',
  'None available',
];

/** Fully met and Partially met are the answers that unlock the follow-ups. */
function adhdCriteriaMet(decision) {
  return decision === 'Fully met' || decision === 'Partially met';
}

// DSM-5 ADHD symptom codes and their clinician-facing descriptions, used in
// the Criterion A summary tables and the Symptom Details panel headings.
// Exported for testability — keep in sync with assessment_data_model.py.
export const DSM_CRITERION_HEADERS = [
  'A1a', 'A1b', 'A1c', 'A1d', 'A1e', 'A1f', 'A1g', 'A1h', 'A1i',
  'A2a', 'A2b', 'A2c', 'A2d', 'A2e', 'A2f', 'A2g', 'A2h', 'A2i'
];

export const DSM_SYMPTOM_DESCRIPTIONS = {
  'A1a': 'Often fails to give close attention to details, or makes careless mistakes in schoolwork, work or during other activities',
  'A1b': 'Often has difficulty sustaining attention in tasks or play activities',
  'A1c': 'Often does not seem to listen when spoken to directly',
  'A1d': 'Often does not follow through on instructions and fails to finish schoolwork, chores, or duties in the workplace',
  'A1e': 'Often has difficulty organising tasks and activities',
  'A1f': 'Often avoids, dislikes, or is reluctant to engage in tasks that require sustained mental effort',
  'A1g': 'Often loses things necessary for tasks or activities',
  'A1h': 'Often easily distracted by extraneous stimuli',
  'A1i': 'Often forgetful in daily activities',
  'A2a': 'Often fidgets with or taps hands or feet or squirms in seat',
  'A2b': 'Often leaves seat in situations when remaining seated is expected',
  'A2c': 'Often feels restless',
  'A2d': 'Often unable to play or take part in leisure activities quietly',
  'A2e': 'Is often "on the go" acting as if "driven by a motor"',
  'A2f': 'Often talks excessively',
  'A2g': 'Often blurts out an answer before a question has been completed',
  'A2h': 'Often has difficulty awaiting their turn',
  'A2i': 'Often interrupts or intrudes on others'
};

export default function DiagnosticDecision() {
  const { patientId } = useParams();
  // Preview mode (?preview=true from the overview's Preview button): the
  // preview-aware navigate keeps the flag on every DD step hop, and the flag
  // is fed into useCanEditStage below so the assigned clinician is read-only
  // too. Same wiring as the Adult Autism DD wizard.
  const navigate = usePreviewAwareNavigate();
  const isPreviewMode = useIsPreviewMode();
  const { patientDataModel, fetchPatientData, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  // CYP DD is unconditionally multi-step (the 4-stage wizard ending at Final
  // Formulation). Override the adult hasMultiStepDD — which reads from the
  // immutable ddFlow stamp and may be false on older patients — with the
  // CYP-specific cypHasMultiStepDD so every CYP patient gets the wizard.
  const { cypHasMultiStepDD, ...baseWf } = useWorkflowConfig(patientDataModel, claims);
  const wf = { ...baseWf, hasMultiStepDD: cypHasMultiStepDD };

  // CYP under-13 (set by CypAdhdDdBridge, absent on adult patients): the SSI
  // never asked the onset question — being inside the age-12 window IS onset
  // — so duration and onset carry one determination. The summary tables and
  // the row popup show it once, under a merged title, instead of printing the
  // same answer in two columns.
  const mergeOnsetColumns = patientDataModel?.cypOnsetImplied === true;

  /*
   * CRITERION E IS NEVER PRE-SELECTED
   * =================================
   *
   * Same rule as the adult V2 (multi-step DD) workflow. ADHD is the
   * condition the child was referred for, so neither that nor any emerging
   * differential tells us whether the symptoms are better explained by
   * something else. The clinician must choose Yes or No themselves, and the
   * banner on the Criterion E panel says so. Only a value they saved earlier
   * is ever shown pre-selected.
   *
   * Criteria A-D are still pre-filled from the Semi-Structured Interview
   * counts, because those are mechanical totals rather than a judgement.
   */

  // Read-only gating. The shared hook compares auth.currentUser against
  // clinicians.clinicianDD (exactly what the previous inline effect did) and
  // additionally forces false while previewing, so a previewer can never
  // write even if they are the assigned DD clinician.
  const canEdit = useCanEditStage(patientDataModel, 'DD', { previewMode: isPreviewMode });

  // Tracks whether the clinician has changed any criteria, diagnosis, or
  // notes on this page. Drives selective regeneration gating (skip the
  // Cloud Function call when nothing changed and a paragraph already exists)
  // and the navigation guard's dirty-state registration.
  const [hasChanges, setHasChanges] = useState(false);

  // Tracks whether the clinician has manually clicked Criterion E on this
  // page visit.  Prevents the org-detection useEffect from overwriting
  // their explicit choice during the window between the optimistic
  // setSelections and the debounced Firestore write landing.  Mirrors
  // the lastAppliedCriterionE pattern in 3_RDD.js.
  const userSetCriterionERef = useRef(false);

  // ---------------------------------------------------------------------------
  // "Entered" marker for the DD module (multi-step orgs: TOC, SLaM, 3 Borough)
  // ---------------------------------------------------------------------------
  // The Patient Overview card shows step-based DD progress (25% once the module
  // has been entered, so the button reads "Continue" rather than "Start"). For
  // multi-step DD the natural entry point is the read-only Review page, but the
  // clinician may also land here first (e.g. the SSI "Go back to Diagnostic
  // Decision" CTA). We record entry by promoting the stage status
  // notStarted -> inProgress, mirroring the Adult Autism DD module's
  // maybePromoteStatus pattern. This adds no new schema field, and every ADHD
  // consumer of DD status only branches on 'completed'/'locked'/'notStarted',
  // so 'inProgress' is safe. The ref makes it a one-shot write per mount.
  // Previewing never counts as entering the module (see 0_DDReview), so the
  // promotion is skipped whenever ?preview=true is set.
  const hasPromotedDDStatusRef = useRef(false);
  useEffect(() => {
    if (hasPromotedDDStatusRef.current) return;
    if (isPreviewMode) return;
    if (!patientDataModel || !wf.hasMultiStepDD) return;
    const uid = auth.currentUser?.uid;
    if (!uid || !isClinicianAssignedToStage(patientDataModel, uid, 'DD')) return;
    const ddStatus = patientDataModel?.dataModel?.['4_diagnosticDecision']?.status;
    if (ddStatus !== 'notStarted') return; // don't clobber locked/inProgress/completed
    hasPromotedDDStatusRef.current = true;
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': { status: 'inProgress' },
    });
  }, [patientDataModel, wf.hasMultiStepDD, patientId, updatePatientFirestore, isPreviewMode]);

  // Add state for ADHD diagnosis tracking
  const [isAdhdAfterCI, setIsAdhdAfterCI] = useState(true);

   // Check for ADHD diagnosis
   useEffect(() => {
    const useAdhdFlow = isADHDProvisionalPrimaryDiagnosis(patientDataModel);
    console.log('🔍 DD: ADHD Flow check - Using ADHD Flow:', useAdhdFlow);
    setIsAdhdAfterCI(useAdhdFlow);
  }, [patientDataModel]);

  // (moved) Prefetch effect declared later after hooks/values are defined


  // Flat-model and deferred-DF orgs always need to see the full ADHD criteria
  // flow because they always complete the SSI (an ADHD-specific deep-dive).
  // This explicit flag protects against future changes to
  // `isADHDProvisionalPrimaryDiagnosis` that might return false for a null PPD.
  const shouldShowAdhdFlow = isAdhdAfterCI || wf.isDFDeferred || wf.isFlatDifferentials;

  // Safety-net hook: triggers AI paragraph regeneration on unmanaged exits
  // (breadcrumb, top nav, browser back) and registers dirty state so the
  // navigation guard can warn before losing unsaved changes.
  //
  // NOTE: the actual hook call lives further down (after `hasUnresolvedMismatch`
  // is computed) so we can pass `forceDirty` to arm the navigation guard on an
  // unresolved SSI decision mismatch even when no regeneration-relevant edits
  // have been made. `markRegenHandled` is only consumed later (in the navigation
  // handlers), so the later call site is safe.

  // ── TOC exit interceptor ──────────────────────────────────────────
  // For TOC patients, breadcrumb / top-nav / sidebar exits with unsaved
  // changes must go through the same save-and-confirm flow as the forward
  // nav button. We register a custom interceptor with the navigation guard
  // context so that instead of showing the generic "leaving section" dialog,
  // it stashes the destination and invokes handleFinishDD (defined later),
  // which saves selections and opens the completion dialog.
  const { registerExitInterceptor, clearExitInterceptor } = useContext(ProformaNavigationGuardContext);

  // We store the interceptor in a ref so the registration effect doesn't
  // depend on handleFinishDD (which has its own deep dependency list) and
  // avoids re-registering on every render.
  const exitInterceptorRef = useRef(null);

  // Refs mirrored each render so the interceptor (stored in a ref, registered
  // once) always reads the latest values without re-registering / going stale.
  const attemptExitRef = useRef(null);
  const hasChangesRef = useRef(false);

  useEffect(() => {
    if (!canEdit) return;

    exitInterceptorRef.current = (destinationUrl, onConfirmCallback) => {
      // `proceed` preserves the pre-existing dirty-aware behaviour:
      //  - real unsaved edits → save-and-confirm completion flow (which also
      //    carries any onConfirmCallback, e.g. signOut, through to
      //    handleSaveAndContinue);
      //  - a callback-driven exit with no edits (e.g. logout) → run the callback
      //    directly (this is the only reason the interceptor now fires without
      //    real edits: forceDirty arms the guard for an unresolved mismatch);
      //  - otherwise → a plain navigate, so a mismatch-only exit never
      //    spuriously completes the DD.
      const proceed = () => {
        if (hasChangesRef.current && handleFinishDDRef.current) {
          setPendingExitRoute(destinationUrl);
          pendingExitCallbackRef.current = onConfirmCallback || null;
          handleFinishDDRef.current();
        } else if (onConfirmCallback) {
          onConfirmCallback();
        } else {
          navigate(destinationUrl);
        }
      };

      // Safety valve: a callback-driven exit (logout) must never be trapped
      // behind the mismatch dialog. Skip the gate but keep the normal save flow.
      // The unresolved mismatch persists and re-prompts on the next managed exit
      // / return to the page.
      if (onConfirmCallback) {
        proceed();
        return;
      }

      // Enforce SSI-decision-mismatch resolution first (no-op for non-mismatch
      // orgs / when already resolved), then run the path's normal behaviour.
      if (attemptExitRef.current && attemptExitRef.current(proceed)) return;
      proceed();
    };

    registerExitInterceptor(exitInterceptorRef.current);
    return () => clearExitInterceptor();
  }, [canEdit, registerExitInterceptor, clearExitInterceptor, navigate]);

  // Ref to forward the handleFinishDD callback into the exit interceptor
  // without creating a circular dependency.
  const handleFinishDDRef = useRef(null);

  // Stores the optional onConfirmCallback (e.g. signOut for logout) passed
  // by guardedNavigate, so handleSaveAndContinue can invoke it.
  const pendingExitCallbackRef = useRef(null);

  // Formulation notes recorded on the SSI Review / DF page for deferred-DF
  // orgs.  Displayed read-only alongside Supporting Documents so the MDT panel
  // can reference the assessing clinician's reasoning during the DD review.
  const formulationNotes = useMemo(() => {
    if (!wf.isDFDeferred) return '';
    return patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[12]?.clinicianNotes?.['Additional Notes'] || '';
  }, [wf.isDFDeferred, patientDataModel]);

  // Internal case notes recorded on the SSI Review page for deferred-DF orgs.
  // Displayed read-only alongside formulation notes for MDT reference.
  const internalCaseNotes = useMemo(() => {
    if (!wf.isDFDeferred) return '';
    return patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections?.['3']?.internalCaseNotes || '';
  }, [wf.isDFDeferred, patientDataModel]);

  // Keep "has other conditions" in sync with PatientOverview and 2_CSD by
  // using the shared routing helper. This prevents route drift dead-ends.
  // When the org skips differentials decisions (cypAdhdFlow, TOC) this is
  // false even with emerging differentials present, so every "next" route,
  // button label and sidebar index below goes straight to Final Formulation.
  const hasOtherConditions = useMemo(() => (
    hasActionableOtherConditions({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      skipDifferentialsDecisions: wf.cypSkipDifferentialsDecisions,
    })
  ), [patientDataModel, wf.isFlatDifferentials, wf.cypSkipDifferentialsDecisions]);

  // DDSideNavbar sections — only built for multi-step DD patients
  // (currentSectionIndex=1, "Decision for ADHD" is the active page).
  const ddNavSections = useMemo(() => {
    if (!wf.hasMultiStepDD) return null;
    return buildDDNavSections({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      isTOCWorkflow: wf.isTOCWorkflow,
      isSLaMWorkflow: wf.isSLaMWorkflow,
      isMDTReview: wf.isMDTReview,
      isSembleMdtReportEnabled: false /* CYP: MDT Plan descoped (22 Aug) */,
      skipDifferentialsDecisions: wf.cypSkipDifferentialsDecisions,
      currentSectionIndex: 1,
    });
  }, [wf.hasMultiStepDD, wf.isTOCWorkflow, wf.isSLaMWorkflow, wf.isMDTReview, patientDataModel, wf.isFlatDifferentials, wf.cypSkipDifferentialsDecisions]);

  // Navigate between DD pages from the sidebar. For TOC patients with
  // unsaved changes, the navigation is intercepted: we stash the intended
  // destination, run the same save-and-confirm flow as the forward nav
  // button, and only navigate after the clinician confirms in the dialog.
  //
  // Index→route mapping must respect hasOtherConditions: when Other Conditions
  // is omitted from the sidebar, Final Formulation is index 2 (not 3). A
  // hard-coded 4-route list sent clinicians to /other-conditions, which then
  // redirected back to ADHD — looking like Final Formulation "wouldn't open".
  // Whether MDT Plan appears in the sidebar for this patient.
  const ddSection1Data = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['1'];
  const hasMdtPlan = false /* CYP: MDT Plan descoped (22 Aug) */ && isAdhdPositiveDecision(ddSection1Data);

  const navigateToDDSection = useCallback(async (index) => {
    const destination = getDDRouteForSectionIndex(patientId, index, hasOtherConditions, hasMdtPlan);

    // The sidenav's existing dirty-aware behaviour, wrapped so we can gate it
    // behind the SSI-decision-mismatch dialog first (via attemptExitRef, kept in
    // sync each render to avoid a temporal-dead-zone reference to attemptExit,
    // which is declared later in the component).
    const proceed = () => {
      if (hasChanges && canEdit && handleFinishDDRef.current) {
        setPendingExitRoute(destination);
        handleFinishDDRef.current();
        return;
      }
      flushPendingUpdates();
      navigate(destination);
    };

    if (attemptExitRef.current && attemptExitRef.current(proceed)) return;
    proceed();
  }, [patientId, navigate, flushPendingUpdates, hasChanges, canEdit, hasOtherConditions, hasMdtPlan]);

  const [nextSteps, setNextSteps] = useState('');
  // const [activeRecording, setActiveRecording] = useState(false);
  // const [recognition, setRecognition] = useState(null);
  const textAreaRef = useRef(null);

  // Pop-up state for the TOC symptom detail overlay.
  // When non-null, contains all data needed to render the pop-up for one
  // DSM criterion row: { criterion, symptom, adulthoodPresent, childhoodPresent,
  //   settings, key } where `key` is the outputs map key (e.g. '3_1_1').
  const [activeSymptomPopup, setActiveSymptomPopup] = useState(null);

  const outputs = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['2']?.outputs || {};

  const keys = [
    '3_1_1', '3_1_2', '3_1_3', '3_1_4', '3_1_5', '3_1_6', '3_1_7', '3_1_8', '3_1_9',
    '3_1_10', '3_1_11', '3_1_12', '3_1_13', '3_1_14', '3_1_15', '3_1_16', '3_1_17', '3_1_18'
  ];

  const headers = DSM_CRITERION_HEADERS;
  const descriptions = DSM_SYMPTOM_DESCRIPTIONS;

  // Lookup map from DSM criterion code (e.g. 'A1a') to its output key (e.g. '3_1_1').
  // Built once from the parallel headers/keys arrays — avoids per-render O(n) searches
  // when rendering table rows.
  const criterionToKey = useMemo(() => {
    const map = {};
    headers.forEach((h, i) => { map[h] = keys[i]; });
    return map;
  }, [headers, keys]);

  // Builds the payload passed to setActiveSymptomPopup when a symptom row is clicked.
  const buildSymptomPopupData = useCallback((item) => ({
    criterion:        item.criterion,
    symptom:          item.symptom,
    adulthoodPresent: item.adulthood,
    childhoodPresent: item.childhood,
    settings:         item.presentInSettings_Adulthood || [],
    key:              criterionToKey[item.criterion],
    isDomain:         false,
  }), [criterionToKey]);

  // Builds the payload for a domain of functional impairment row click.
  // Domains have LLM-generated paragraphs stored under keys like ssi_unified_3_2_1;
  // the key field carries the domain_key_suffix so the popup can look them up.
  const buildDomainPopupData = useCallback((item) => ({
    criterion:        null,
    symptom:          item.domain,
    adulthoodPresent: item.adulthood,
    childhoodPresent: item.childhood,
    settings:         [],
    key:              item.key,
    isDomain:         true,
  }), []);

  const currentSectionNumber = 1
  const recommendedDiagnosisSectionNumber = 3

  const [selectedReport, setSelectedReport] = useState(null);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);

  // Function to open the dialog with a specific report
  const handleOpenDocumentDialog = (reportStage) => {
    setSelectedReport(reportStage);
    setIsDocumentDialogOpen(true);
  };

  // Function to close the dialog
  const handleCloseDocumentDialog = () => {
    setIsDocumentDialogOpen(false);
    // Optional: Reset selected report after dialog closes
    setTimeout(() => setSelectedReport(null), 300);
  };

  const SECTIONS_SSI = useMemo(() => Object.entries(patientDataModel['dataModel']['3_semiStructuredInterview']['sections']).map(([key, section]) => ({
    key,
    ...section
  })), [patientDataModel]);

  // Prefetch hooks for Supporting Documents
  const ciLastGenerated = useMemo(() => patientDataModel?.documents?.clinicalinterviewReport?.lastGenerated || null, [patientDataModel]);
  const ssiLastGenerated = useMemo(() => patientDataModel?.documents?.semistructuredinterviewReport?.lastGenerated || null, [patientDataModel]);
  const qbTestLastGenerated = useMemo(() => patientDataModel?.documents?.qbtestReport?.lastGenerated || null, [patientDataModel]);
  const qbCheckLastGenerated = useMemo(() => patientDataModel?.documents?.qbcheckReport?.lastGenerated || null, [patientDataModel]);
  const docs = useMemo(() => patientDataModel?.documents || {}, [patientDataModel]);
  // Hide any supporting document whose report section has regressed to the
  // purple "AI outputs ready for review" state (was approved, then edits
  // regenerated paragraphs) - the rendered PDF may be stale.
  const manageReportsForDocs = useMemo(() => patientDataModel?.dataModel?.['6_manageReports'], [patientDataModel]);
  const showCI = useMemo(() => Boolean(docs.clinicalinterviewReport) && !isSupportingDocStale(manageReportsForDocs, 'clinicalinterviewReport'), [docs, manageReportsForDocs]);
  const showSSI = useMemo(() => Boolean(docs.semistructuredinterviewReport) && !isSupportingDocStale(manageReportsForDocs, 'semistructuredinterviewReport'), [docs, manageReportsForDocs]);
  const showQbTest = useMemo(() => Boolean(docs.qbtestReport) && !isSupportingDocStale(manageReportsForDocs, 'qbtestReport'), [docs, manageReportsForDocs]);
  const showQbCheck = useMemo(() => Boolean(docs.qbcheckReport) && !isSupportingDocStale(manageReportsForDocs, 'qbcheckReport'), [docs, manageReportsForDocs]);
  const { prefetch: prefetchCI } = useEmbeddedReportUrl(patientId, 'Clinical Interview', ciLastGenerated, auth.currentUser?.uid);
  const { prefetch: prefetchSSI } = useEmbeddedReportUrl(patientId, 'Semi-Structured Interview', ssiLastGenerated, auth.currentUser?.uid);
  const { prefetch: prefetchQbTest } = useEmbeddedReportUrl(patientId, 'QbTest', qbTestLastGenerated, auth.currentUser?.uid);
  const { prefetch: prefetchQbCheck } = useEmbeddedReportUrl(patientId, 'QbCheck', qbCheckLastGenerated, auth.currentUser?.uid);

  // Get the recommended diagnosis section data
  const recommendedDiagnosisSection = useMemo(() => {
    return SECTIONS_SSI.find(section => section.sectionNumber === recommendedDiagnosisSectionNumber) || {};
  }, [SECTIONS_SSI, recommendedDiagnosisSectionNumber]);

  // Check if we have valid recommendation data
  const hasRecommendedDiagnosis = useMemo(() => {
    return Boolean(recommendedDiagnosisSection?.diagnosisDecision);
  }, [recommendedDiagnosisSection]);

  // Prefetch CI/SSI when Supporting Documents panel becomes visible.
  // For deferred DF orgs, RDD is skipped so hasRecommendedDiagnosis is false,
  // but we still want to prefetch because the panel is shown via isDFDeferred.
  // TOC patients have already reviewed supporting documents on the MDT Review
  // page, so the panel is suppressed here and prefetching is skipped.
  useEffect(() => {
    if (shouldShowAdhdFlow && (hasRecommendedDiagnosis || wf.isDFDeferred) && !wf.hasMultiStepDD) {
      prefetchCI?.();
      prefetchSSI?.();
    }
  }, [shouldShowAdhdFlow, hasRecommendedDiagnosis, wf.isDFDeferred, wf.hasMultiStepDD, prefetchCI, prefetchSSI]);

  const finalDiagnosisSection = useMemo(() => {
    return patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[currentSectionNumber] || {};
  }, [patientDataModel, currentSectionNumber]);

  console.log("finalDiagnosisSection", finalDiagnosisSection)

  const recommendedByName = useMemo(() => {
    return patientDataModel?.dataModel?.['6_manageReports']?.ssiReportApprovedByName || 'Unknown';
  }, [patientDataModel]);

  // Name and timestamp of the clinician who approved the SSI report paragraphs.
  // The symptom/domain popup shows SSI paragraphs (ssi_unified_*), so the
  // attribution should reference the SSI report approval, not DD.
  const ssiReportApprovedByName = useMemo(
    () => patientDataModel?.dataModel?.['6_manageReports']?.ssiReportApprovedByName || null,
    [patientDataModel]
  );
  const ssiReportApprovedAt = useMemo(
    () => patientDataModel?.dataModel?.['6_manageReports']?.ssiReportApprovedAt || null,
    [patientDataModel]
  );


  const questionsSymptoms = useMemo(() => {
    const section = SECTIONS_SSI.find(
      section => section.sectionTitle === "Symptoms"
    );
    if (!section) {
      console.error(`Section with title Symptoms not found.`);
      return [];
    }
    return Object.entries(section.questions || {}).map(([key, question]) => question);
  }, [SECTIONS_SSI]);

  const dataDomains = useMemo(() => {
    const section = SECTIONS_SSI.find(
      section => section.sectionTitle === "Domains of Functional Impairment"
    );
    if (!section) {
      console.error(`Section with title Domains of Functional Impairment not found.`);
      return [];
    }
    return section
  }, [SECTIONS_SSI]);

  const domainImpairment = useMemo(() => {
    if (!dataDomains?.questions) return {
      dsmCriterionD_FunctionalImpairment: false
    };

    let adulthoodDomainsCount = 0;

    // Iterate through each question
    Object.values(dataDomains.questions).forEach(question => {
      // Check if there are any examples selected for adulthood
      if (question.functioningReduced_Adulthood) {
        adulthoodDomainsCount++;
      }
    });

    return {
      dsmCriterionD_FunctionalImpairment: adulthoodDomainsCount >= 1
    };
  }, [dataDomains]);

  // Structured domain-level impairment data for the summary table.
  // Each domain has a title, adulthood/childhood impairment flags, and the
  // output key suffix (e.g. '3_2_1') used to look up LLM-generated paragraphs
  // from the SSI outputs object (keys: ssi_unified_3_2_1 or ssi_Adulthood_3_2_1).
  const domainImpairmentData = useMemo(() => {
    if (!dataDomains?.questions) return [];
    return Object.entries(dataDomains.questions)
      .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
      .map(([key, question]) => ({
        domain: question.DSM_symptom || question.DSM_symptom_prefix || 'Unknown',
        adulthood: question.functioningReduced_Adulthood,
        childhood: question.functioningReduced_Childhood,
        // '3_2_' prefix matches the domain_key_suffix used in llm_processor.py
        // when storing paragraphs: e.g. ssi_unified_3_2_1, ssi_Adulthood_3_2_1
        key: `3_2_${key}`,
      }));
  }, [dataDomains]);

  // Calculate if criterion C is met (symptoms across settings)
  const criterionCMet = useMemo(() => {
    // Skip if no symptom data
    if (!questionsSymptoms || questionsSymptoms.length === 0) return null;

    // Count symptoms present in each setting
    const settingCounts = {
      'Familial': 0,
      'Social': 0,
      'Occupational': 0,
      'Educational': 0,
      'Community': 0
    };

    // Iterate through all symptoms
    questionsSymptoms.forEach(symptom => {
      // Only count symptoms present in adulthood
      if (symptom.symptomPresent_Adulthood === true) {
        // Check which settings this symptom occurs in
        const presentInSettings = symptom.presentInSettings_Adulthood || [];

        // Increment counter for each setting this symptom is present in
        presentInSettings.forEach(setting => {
          if (setting in settingCounts) {
            settingCounts[setting]++;
          }
        });
      }
    });

    // Count how many settings have at least 3 symptoms
    const settingsWithThreePlusSymptoms = Object.values(settingCounts).filter(count => count >= 3).length;

    // Log for debugging
    // console.log("Setting counts:", settingCounts, "Settings with 3+ symptoms:", settingsWithThreePlusSymptoms);

    // Criterion C is met if at least 2 settings have 3+ symptoms
    return settingsWithThreePlusSymptoms >= 2;
  }, [questionsSymptoms]);

  const attentionDeficitData = questionsSymptoms
    ? questionsSymptoms.filter(item => item.DSM_code.startsWith('A1')).map(item => ({
      criterion: item.DSM_code,
      symptom: item.DSM_symptom,
      adulthood: item.symptomPresent_Adulthood,
      childhood: item.symptomPresent_Childhood,
      presentInSettings_Adulthood: item.presentInSettings_Adulthood,
    }))
    : [];

  const hyperactivityImpulsivityData = questionsSymptoms
    ? questionsSymptoms.filter(item => item.DSM_code.startsWith('A2')).map(item => ({
      criterion: item.DSM_code,
      symptom: item.DSM_symptom,
      adulthood: item.symptomPresent_Adulthood,
      childhood: item.symptomPresent_Childhood,
      presentInSettings_Adulthood: item.presentInSettings_Adulthood,
    }))
    : [];

  const calculateTotals = (data) => {
    // Strictly a clear Yes: the CYP SSI also records 'inconclusive', which
    // must never count toward a criterion pre-fill.
    const adulthoodTotal = data.filter(item => item.adulthood === true).length;
    const childhoodTotal = data.filter(item => item.childhood === true).length;
    return { adulthoodTotal, childhoodTotal };
  };

  const attentionTotals = calculateTotals(attentionDeficitData);
  const hyperactivityTotals = calculateTotals(hyperactivityImpulsivityData);

  // Check if SSI reset is required (Lead clinician has triggered reset due to conflict).
  // When true, we hide SSI-related panels and show only DSM criteria without prefills.
  // Uses shouldShowAdhdFlow (not isAdhdAfterCI) so that flat-model and deferred-DF
  // orgs correctly enter reset mode — isAdhdAfterCI is always false for flat-model
  // patients since there is no PPD, but the ADHD criteria panel is still visible.
  const isSSIResetMode = useMemo(() => {
    const ssiResetRequired = patientDataModel?.dataModel?.['6_manageReports']?.ssiResetRequired === true;
    return ssiResetRequired && shouldShowAdhdFlow;
  }, [patientDataModel, shouldShowAdhdFlow]);

  const questionPanels = useMemo(() => {
    
    // SSI-derived prefills (A-D) are computed from Symptoms and Domains data
    // and are available even for deferred-DF workflows (e.g. TOC v2) where no
    // RDD step exists.  Only disabled in SSI reset mode where the lead
    // clinician must decide all criteria from scratch.
    const shouldUseSsiPrefills = !isSSIResetMode;

    // RDD/DF-dependent prefills (Criterion E value, diagnosis recommendations)
    // require a completed Recommended Diagnosis Decision or Diagnostic
    // Formulation, so they are disabled for deferred-DF orgs where neither
    // has been completed at this point in the workflow.
    const shouldUseRddPrefills = !isSSIResetMode && !wf.isDFDeferred;
    
    // Symptom count for Criterion A follows age: six symptoms before 17,
    // five from 17 (the adult bar). Unknown date of birth keeps six, so a
    // missing age never quietly lowers the threshold.
    const symptomThreshold = patientDataModel?.cypAgeUnder17 === false ? 5 : 6;
    const inattentionMet = shouldUseSsiPrefills
      ? attentionTotals.adulthoodTotal >= symptomThreshold
      : null;
    const hyperactivityMet = shouldUseSsiPrefills
      ? hyperactivityTotals.adulthoodTotal >= symptomThreshold
      : null;

    return [
    {
      title: "Criterion A: Symptom Threshold",
      tooltip: "Symptom duration must be ≥6 months.\n\nSymptoms are inconsistent with the patient's developmental level.\n\nConsider masking, scaffolding, and environmental compensation.",
      questions: [
        {
          name: `Have ≥${symptomThreshold} inattention symptoms been present?`,
          prefill: inattentionMet,
          key: "dsmCriterionA_SymptomThreshold_A",
          isOptional: false,
        },
        {
          name: `Have ≥${symptomThreshold} hyperactivity-impulsivity symptoms been present?`,
          prefill: hyperactivityMet,
          key: "dsmCriterionA_SymptomThreshold_HI",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion B: Age of Onset",
      tooltip: "Symptoms do not need to have caused impairment before age 12 - only to have been present.\n\nSymptoms may have been present before age 12 but were not recognised or identified at the time.\n\nRetrospective confirmation from parents, carers, or school records is acceptable.",
      questions: [
        {
          name: 'Are there / were there several (≥3) symptoms present before the age of 12?',
          prefill: shouldUseSsiPrefills ? attentionTotals.childhoodTotal + hyperactivityTotals.childhoodTotal >= 3 : null,
          key: "dsmCriterionB_AgeOfOnset",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion C: Symptoms Across Settings",
      tooltip: "Consider whether environmental adaptations, scaffolding or masking may affect how symptoms present across settings.",
      questions: [
        {
          name: 'Are several (≥3) symptoms present in ≥2 settings?',
          prefill: shouldUseSsiPrefills ? (hasRecommendedDiagnosis ? recommendedDiagnosisSection.dsmCriterionC_SymptomsAcrossSettings : criterionCMet) : null,
          key: "dsmCriterionC_SymptomsAcrossSettings",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion D: Functional Impairment",
      tooltip: "Consider impairment relative to their age and developmental level and across relevant areas of everyday functioning.\n\nDomains of impairment refer to areas of function, for example, psychological, interpersonal relationships, education, and risk awareness.\n\nFor the purposes of a NICE-aligned diagnostic pathway, symptoms must be associated with at least moderate impairment for an ADHD diagnosis to be made.",
      questions: [
        {
          name: 'Do the symptoms cause at least moderate personal, social, and/or educational impairment?',
          prefill: shouldUseSsiPrefills ? (hasRecommendedDiagnosis ? recommendedDiagnosisSection.dsmCriterionD_FunctionalImpairment : domainImpairment.dsmCriterionD_FunctionalImpairment) : null,
          key: "dsmCriterionD_FunctionalImpairment",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion E: Exclusion of Other Disorders",
      questions: [
        {
          name: 'The symptoms cannot be (better) explained by another psychiatric disorder and are not due to the effect of a substance or medication.',
          // NEVER pre-selected for CYP (Jasmine's banner: clinical judgement
          // only) — the adult RDD carry-over does not apply to children.
          prefill: null,
          key: "dsmCriterionE_NotOtherExplanation",
          isOptional: false,
        },
      ],
      tooltip: "ADHD can co-occur with other conditions; Criterion E concerns whether the symptoms are better explained by another condition.",
    },
    {
      title: "Diagnostic Decision",
      // The content doc's NICE clinical alert sits with the decision.
      tooltip: "NICE Clinical Alert: A diagnosis should not be withheld simply because a child is achieving academically, particularly where compensatory strategies or high cognitive ability camouflage underlying difficulties. Functional impact may also be reflected in emotional strain, exhaustion, or reduced wellbeing despite maintained performance.",
      // Question indices match adult ADHD and are used by showWhen, the
      // clearing cascade, the completion check and the summary:
      // 0 decision, 1 presentation, 2 severity, 3 partial classification,
      // 4 other-specified reason, 5 other-reason text, 6 supporting evidence.
      questions: [
        {
          id: 'diagnose_adhd',
          name: 'Has the criteria for ADHD Diagnosis Been Met?',
          // "Partially met" is offered unless the patient's stamped CYP
          // workflow says to hide it (cypAdhdFlow.hidePartiallyMet, WLC and
          // TOC from Oct 2026). The stamp is fixed at patient creation, so a
          // child who recorded "Partially met" before the flag keeps seeing
          // it; the follow-ups that depend on it (indices 3-5) simply never
          // become reachable when the option is absent.
          radio_options: [
            { value: 'Fully met' },
            ...(wf.cypDdHidePartiallyMet ? [] : [{
              value: 'Partially met',
              tooltip: "Significant symptoms and impairment present",
            }]),
            {
              value: 'Not met',
              tooltip: "Criteria not met for ADHD diagnosis",
            },
          ],
          prefill: null,
          key: "diagnosisDecision",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'select_adhd_subtype',
          name: 'Presentation',
          subtitle: "Select the appropriate presentation based on current symptoms",
          radio_options: [
            { value: 'Predominantly Inattentive Presentation' },
            { value: 'Predominantly Hyperactive-Impulsive Presentation' },
            { value: 'Combined Presentation' },
          ],
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => selections[5][0] === 'Fully met',
          key: "diagnosisPresentation",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'select_adhd_severity',
          name: 'Severity of Symptoms',
          subtitle: "Select the appropriate level of severity based on the extent of impairment in daily life.",
          radio_options: [
            { value: 'Moderate' },
            { value: 'Severe' },
          ],
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => selections[5][0] === 'Fully met',
          key: "diagnosisSeverity",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'select_partial_adhd_type',
          name: 'Diagnostic Classification',
          subtitle: "Select the appropriate classification for partial criteria",
          // Each option carries its own tooltip, same shape as adult Decision
          // for ADHD. The partial-remission wording is the CYP one (a prior
          // full diagnosis that has since fallen below the symptom
          // threshold); the other two follow the adult tooltips.
          radio_options: [
            {
              value: 'ADHD in Partial Remission',
              tooltip: "Only select if the patient previously met full diagnostic criteria for ADHD, but currently does not meet the full symptom threshold.",
            },
            {
              value: 'Other Specified ADHD',
              tooltip: "Symptoms cause significant impairment but do not meet full ADHD criteria. Clinician must specify the reason.",
            },
            {
              value: 'Unspecified ADHD',
              tooltip: "Used when insufficient information/time to clarify (e.g., emergency settings).",
            },
          ],
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => selections[5][0] === 'Partially met',
          key: "partialDiagnosisClassification",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'specify_other_adhd_reason',
          name: 'Specify Reason for Other Specified ADHD',
          subtitle: "Select the reason this patient meets Other Specified ADHD criteria",
          radio_options: [
            { value: 'Subthreshold symptoms with impairment' },
            { value: 'Atypical symptom patterns' },
            { value: 'Late onset symptoms (first presentation after age 12)' },
            { value: 'Symptom overlap with another disorder' },
            { value: 'Symptoms that cause impairment in one setting' },
            { value: 'Symptom variability' },
            { value: 'Other' },
          ],
          prefill: null,
          dependence: ['select_partial_adhd_type'],
          showWhen: (selections) => selections[5][0] === 'Partially met'
            && selections[5][3] === 'Other Specified ADHD',
          key: "otherSpecifiedReason",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'other_reason_text',
          name: 'Specify Other Reason',
          subtitle: "Please provide details for the other reason selected above",
          prefill: null,
          dependence: ['specify_other_adhd_reason'],
          showWhen: (selections) => selections[5][0] === 'Partially met'
            && selections[5][3] === 'Other Specified ADHD'
            && selections[5][4] === 'Other',
          isTextInput: true,
          key: "otherSpecifiedReasonText",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'select_supporting_evidence',
          name: 'Supporting Evidence',
          subtitle: "Is the diagnosis supported by collateral information?",
          checklist_options: CYP_SUPPORTING_EVIDENCE.map((value) => ({ value })),
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => adhdCriteriaMet(selections[5][0]),
          key: "supportedByCollateral",
          isOptional: true,
          recommendedSelection: null,
        },
      ],
    },
  ];
  }, [hasRecommendedDiagnosis, attentionTotals, hyperactivityTotals, domainImpairment, criterionCMet, recommendedDiagnosisSection, isSSIResetMode, wf.isDFDeferred, wf.cypDdHidePartiallyMet, patientDataModel?.cypAgeUnder17]);


  const [selections, setSelections] = useState(() => {
    console.log('🔍 DD: Selections initialization - wf.isSSIFirst:', wf.isSSIFirst, 'isAdhdAfterCI:', isAdhdAfterCI, 'hasRecommendedDiagnosis:', hasRecommendedDiagnosis);
    
    // Get saved selections from localStorage as fallback (currently commented out)
    // const savedSelections = localStorage.getItem(`selections_${patientId}`);

    // Initialize selections array based on questionPanels structure
    return questionPanels.map((panel, panelIndex) =>
      panel.questions.map((question, questionIndex) => {
        // If question has a key, check for value in finalDiagnosisSection first
        if (question.key && finalDiagnosisSection) {
          // Special case for supportedByCollateral which should default to [].
          // Normalize legacy "Partner/family reports" so existing selections
          // are preserved after the label was renamed to "Informant reports".
          if (question.key === 'supportedByCollateral') {
            const raw = finalDiagnosisSection[question.key] ?? [];
            return raw.map(v => v === 'Partner/family reports' ? 'Informant reports' : v);
          }
          if (question.key === 'remissionSpecifier') {
            const raw = finalDiagnosisSection[question.key];
            return Array.isArray(raw) ? raw : [];
          }

          // For all other keys, return finalDiagnosisSection value if available,
          // otherwise fallback to prefill value or null
          const finalValue = finalDiagnosisSection[question.key] ?? question.prefill ?? null;
          
          // Log Criterion E specifically for debugging
          if (question.key === 'dsmCriterionE_NotOtherExplanation') {
            console.log('🔍 DD: Criterion E initialization - Panel:', panelIndex, 'Question:', questionIndex, 'Key:', question.key, 'Firebase value:', finalDiagnosisSection[question.key], 'Prefill value:', question.prefill, 'Final value:', finalValue);
          }
          
          return finalValue;
        }

        // For questions without keys, just use prefill value or null
        return question.prefill ?? null;
      })
    );
  });

  // The DSM Criterion A-D question keys. These are mechanically derived from the
  // Semi-Structured Interview symptom / domain counts (Criterion E is always set
  // by the Lead and never prefilled). Used both to record manual toggles and to
  // drive the post-override refresh below.
  const SSI_DERIVED_CRITERION_KEYS = useMemo(() => new Set([
    'dsmCriterionA_SymptomThreshold_A',
    'dsmCriterionA_SymptomThreshold_HI',
    'dsmCriterionB_AgeOfOnset',
    'dsmCriterionC_SymptomsAcrossSettings',
    'dsmCriterionD_FunctionalImpairment',
  ]), []);

  // Criterion A-D keys the Lead deliberately toggled this session. Session-scoped
  // (a ref, reset on remount) — it covers the current mount immediately, before
  // the debounced write / next fetch lands. Durability across remounts is
  // provided by the persisted `criterionManualOverrides` markers, which the
  // refresh effect unions with this ref.
  const manuallyToggledCriteriaRef = useRef(new Set());

  // Re-prefill Criteria A-D from the LATEST SSI counts while a Lead SSI override
  // is active. Without this, a Lead who completes the DD, then takes over the SSI
  // and changes symptom/domain counts, would return to a DD page showing stale
  // A-D values (they were persisted from the pre-override counts and so no longer
  // fall back to the fresh prefill). We recompute each A-D criterion from the
  // current counts (surfaced via each question's `prefill`) and, for any that has
  // drifted and was not manually overridden, update both local state and
  // Firestore. Converges (only writes on an actual diff) and is a no-op for
  // non-override orgs / patients, so there is zero impact elsewhere.
  //
  // Manual overrides are protected via the UNION of two sources so a deliberate
  // A-D choice is never clobbered:
  //  - the session ref (this mount's toggles, before the write/fetch lands), and
  //  - the DURABLE criterionManualOverrides markers persisted on the DD section
  //    (so a manual choice survives remounts — the ref alone would reset and the
  //    effect would overwrite the manual value back to the count-based prefill).
  useEffect(() => {
    // Inlined override check (rather than the `ssiOverrideActive` const, which is
    // declared later in the component) to avoid a temporal-dead-zone reference.
    const overrideActive = isSSILeadOverrideActive(patientDataModel);
    if (!overrideActive || !canEdit) return;

    const persistedMarkers = finalDiagnosisSection?.criterionManualOverrides || {};
    const protectedKeys = new Set(manuallyToggledCriteriaRef.current);
    Object.keys(persistedMarkers).forEach((key) => {
      if (persistedMarkers[key]) protectedKeys.add(key);
    });

    const { next, persist, changed } = computeCriterionRefresh({
      questionPanels,
      selections,
      criterionKeys: SSI_DERIVED_CRITERION_KEYS,
      manuallyToggledKeys: protectedKeys,
    });

    if (changed) {
      setSelections(next);
      updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: { [currentSectionNumber]: persist },
        },
      });
    }
  }, [patientDataModel, canEdit, questionPanels, selections, patientId, currentSectionNumber, updatePatientFirestore, SSI_DERIVED_CRITERION_KEYS, finalDiagnosisSection]);

  // Semble MDT Report orgs (Well Life Clinic): an ADHD-positive decision
  // ("Yes", or the earlier "Fully met"/"Partially met") routes to the MDT Plan page — the deferred
  // final DD step where patient goals and To-discuss-in-PAD selections are
  // captured for the Semble MDT Report. Module completion happens THERE, not
  // on this page. "Not met" (or a non-ADHD hierarchical outcome) completes
  // here as normal, producing a partial Semble report (Sections 1-2 only).
  const goesToMdtPlan = false /* CYP: MDT Plan descoped (22 Aug) */
    && shouldShowAdhdFlow
    && ['Yes', 'Fully met', 'Partially met'].includes(selections?.[5]?.[0]);

  const handleSelection = async (panelIndex, questionIndex, value) => {
    if (!canEdit) return;

    // Deep-copy the 2D selections array so inner panel arrays are
    // independent of the previous state reference.  A shallow spread
    // ([...selections]) shares inner arrays, which can cause stale-
    // state issues when effects read selections concurrently.
    const newSelections = selections.map(panel => [...panel]);

    // Record that the clinician has explicitly set Criterion E so the
    // org-detection useEffect does not overwrite their choice before
    // the debounced Firestore write lands.
    if (panelIndex === 4 && questionIndex === 0) {
      userSetCriterionERef.current = true;
    }

    // If this is the diagnosis decision question (index 5, question 0)
    if (panelIndex === 5 && questionIndex === 0) {
      newSelections[panelIndex][questionIndex] = value;

      // Fully met and Partially met ask different follow-ups, so a change
      // of decision clears the other branch. Supporting evidence is the same
      // list on both, so ticked sources carry across; Not met clears them.
      newSelections[panelIndex][1] = null; // presentation
      newSelections[panelIndex][2] = null; // severity
      newSelections[panelIndex][3] = null; // partial classification
      newSelections[panelIndex][4] = null; // other-specified reason
      newSelections[panelIndex][5] = null; // other-reason text
      const allowedEvidence = new Set(CYP_SUPPORTING_EVIDENCE);
      const keptEvidence = adhdCriteriaMet(value) && Array.isArray(newSelections[panelIndex][6])
        ? newSelections[panelIndex][6].filter((item) => allowedEvidence.has(item))
        : null;
      newSelections[panelIndex][6] = keptEvidence;

      setSelections(newSelections);

      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              diagnosisDecision: value,
              diagnosisPresentation: null,
              diagnosisSeverity: null,
              partialDiagnosisClassification: null,
              otherSpecifiedReason: null,
              otherSpecifiedReasonText: null,
              supportedByCollateral: keptEvidence,
            },
          },
        },
      });

      // SLaM / 3 Borough: if the ADHD decision does not line up with the
      // Semi-Structured Interview symptom / domain counts, open a decision
      // dialog immediately so the clinician chooses whether to update the SSI
      // now or finish Diagnostic Decision first.
      //
      // Gated on the SSI being completed: the check reads the SSI symptom/domain
      // counts (meaningless on a part-finished SSI) and any reconciliation
      // depends on taking over a completed SSI. SLaM is SSI-first, so in practice
      // the SSI is essentially always completed by the time DD is reached.
      //
      // We reuse the SAME predicate as the exit gate (isUnresolvedSSIDdMismatch)
      // so the two triggers can never diverge: it also suppresses the dialog once
      // a Lead override is active (the Lead already owns the SSI) or once this
      // exact decision has been acknowledged as "keep the SSI as is".
      const ssiCompleted =
        patientDataModel?.dataModel?.['3_semiStructuredInterview']?.status === 'completed';
      if (wf.allowLeadSSIOverride && !isSSIResetMode && ssiCompleted) {
        const ackDecision =
          patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[currentSectionNumber]?.ssiMismatchAcknowledgedDecision
          || null;
        const unresolved = isUnresolvedSSIDdMismatch({
          diagnosisDecision: value,
          attentionDeficitData,
          hyperactivityImpulsivityData,
          domainImpairmentData,
          overrideActive: isSSILeadOverrideActive(patientDataModel),
          acknowledgedDecision: ackDecision,
        });
        if (unresolved) {
          setSsiDdConsistencyDialog({
            open: true,
            diagnosisDecision: value,
          });
        }
      }

      // Path 1 soft mismatch (formal DD vs in-clinic provisional PAD) is no
      // longer surfaced as an interruptive modal here. It is shown as a quiet,
      // non-blocking inline note on this decision panel (see render), and the
      // actionable choice (redo vs leave as is) is confirmed at DD-module
      // completion (3_Formulation.js) — so the clinician books the patient back
      // only after finalising the decision.
    }
    // Partial classification (index 5, question 3): the reason questions
    // only apply to Other Specified ADHD.
    else if (panelIndex === 5 && questionIndex === 3) {
      newSelections[panelIndex][questionIndex] = value;
      if (value !== 'Other Specified ADHD') {
        newSelections[panelIndex][4] = null;
        newSelections[panelIndex][5] = null;
      }
      setSelections(newSelections);

      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              partialDiagnosisClassification: value,
              otherSpecifiedReason: value !== 'Other Specified ADHD' ? null : newSelections[panelIndex][4],
              otherSpecifiedReasonText: value !== 'Other Specified ADHD' ? null : newSelections[panelIndex][5],
            },
          },
        },
      });
    }
    // Other-specified reason (index 5, question 4): free text only for Other.
    else if (panelIndex === 5 && questionIndex === 4) {
      newSelections[panelIndex][questionIndex] = value;
      if (value !== 'Other') {
        newSelections[panelIndex][5] = null;
      }
      setSelections(newSelections);

      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              otherSpecifiedReason: value,
              otherSpecifiedReasonText: value !== 'Other' ? null : newSelections[panelIndex][5],
            },
          },
        },
      });
    }
    else {
      // Handle other selections normally
      newSelections[panelIndex][questionIndex] = value;
      setSelections(newSelections);

      // Get the corresponding question key from questionPanels
      const question = questionPanels[panelIndex].questions[questionIndex];

      // Remember when the Lead manually toggles an SSI-derived Criterion (A-D)
      // so the override-refresh effect never clobbers a deliberate choice with a
      // fresh count-based prefill. We record this BOTH:
      //  - in a session ref (covers the current mount immediately, before the
      //    debounced write / next fetch lands), AND
      //  - as a DURABLE per-criterion marker in Firestore
      //    (criterionManualOverrides), so the choice survives remounts. Without
      //    the durable marker the ref resets on remount and the refresh effect
      //    would silently overwrite the manual value back to the count-based
      //    prefill. The marker is only written for SSI-override orgs (SLaM /
      //    3 Borough) to keep other organisations' documents untouched.
      const isSsiDerivedCriterion =
        question?.key && SSI_DERIVED_CRITERION_KEYS.has(question.key);
      const writeManualOverrideMarker =
        isSsiDerivedCriterion && wf.allowLeadSSIOverride;
      if (isSsiDerivedCriterion) {
        manuallyToggledCriteriaRef.current.add(question.key);
      }

      // Update Firestore with the specific field (plus the durable manual-
      // override marker when applicable — deepMerge preserves any existing
      // markers for other criteria).
      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              [question.key]: value,
              ...(writeManualOverrideMarker
                ? { criterionManualOverrides: { [question.key]: true } }
                : {}),
            },
          },
        },
      });
    }

    // Also save to localStorage as backup
    localStorage.setItem(`selections_${patientId}`, JSON.stringify(newSelections));
    setHasChanges(true);
  };

  const isFormValid = useCallback(() => {
    // Use shouldShowAdhdFlow (not isAdhdAfterCI) to decide which validation
    // path to take.  Flat-model and deferred-DF orgs always show the ADHD
    // criteria panel (shouldShowAdhdFlow = true) even though
    // isADHDProvisionalPrimaryDiagnosis returns false (there is no PPD).
    // Using isAdhdAfterCI here would route flat-model patients into the
    // non-ADHD validation branch which requires NonADHDDiagnosticConfirmation
    // fields that are never rendered — permanently disabling the button.
    if (!shouldShowAdhdFlow) {
      // Hierarchical non-ADHD path: PPD is a non-ADHD condition and the
      // NonADHDDiagnosticConfirmation component is visible.
      const nonAdhdDiagnosisDecision = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[1]?.nonAdhdDiagnosisDecision;
      const nonAdhdDiagnosisNotes = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[1]?.nonAdhdDiagnosisNotes;
      
      if (!nonAdhdDiagnosisDecision || !nonAdhdDiagnosisNotes) return false;  
      
      return true;
    }
    
    // ADHD criteria validation — used by flat-model, deferred-DF, and
    // hierarchical orgs where PPD = ADHD.
    // Early return if any required data is missing
    if (!selections || !questionPanels || !Array.isArray(selections) || !Array.isArray(questionPanels)) {
      console.log('Missing required data for validation:', { selections, questionPanels });
      return false;
    }
  
    try {
      // Criterion E (Exclusion of Other Disorders) must be an explicit Yes/No.
      // Multi-step DD orgs (SLaM, TOC) never auto-prefill it — same hard gate
      // as presentation / severity for Fully met. Standard orgs usually arrive
      // with a prefill, but still cannot Confirm if the clinician cleared it.
      const criterionESelected = selections[4]?.[0];
      if (criterionESelected !== true && criterionESelected !== false) {
        return false;
      }

      // Get diagnostic decision
      const diagnosisDecision = selections[5][0];

      // If no diagnosis decision selected, form is invalid
      if (!diagnosisDecision) return false;

      // Fully met needs a presentation and a severity. Partially met needs
      // a classification, and Other Specified ADHD needs a reason (plus
      // free text when that reason is Other). Not met needs nothing
      // further. Supporting evidence is optional.
      if (diagnosisDecision === 'Fully met') {
        if (!selections[5][1] || !selections[5][2]) return false;
      }
      if (diagnosisDecision === 'Partially met') {
        const classification = selections[5][3];
        if (!classification) return false;
        if (classification === 'Other Specified ADHD') {
          const reason = selections[5][4];
          if (!reason) return false;
          if (reason === 'Other' && !(selections[5][5] || '').trim()) return false;
        }
      }

      // If we reach here, form is valid
      return true;
    } catch (error) {
      console.error('Error in form validation:', error);
      return false;
    }
  }, [selections, questionPanels, shouldShowAdhdFlow, patientDataModel]);

  const [isSaving, setIsSaving] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // When a TOC clinician tries to exit via breadcrumb, top nav, or sidebar
  // while they have unsaved changes, we stash the intended destination here
  // and show the completion dialog. The dialog's "Save & Continue" button
  // then navigates to this route after saving selections and triggering
  // LLM regeneration.
  const [pendingExitRoute, setPendingExitRoute] = useState(null);

  // SSI Conflict Alert state (3 Borough / SLaM workflow)
  // This dialog is shown when the Lead clinician's final DD decision conflicts with SSI recommendation
  const [isConflictAlertOpen, setIsConflictAlertOpen] = useState(false);
  const [conflictDetails, setConflictDetails] = useState(null);
  const [isResettingSSI, setIsResettingSSI] = useState(false);

  // Consistency dialog: opens when Fully / Partially / Not met is selected
  // and the SSI symptom/domain counts do not line up (SLaM / 3 Borough).
  const [ssiDdConsistencyDialog, setSsiDdConsistencyDialog] = useState({
    open: false,
    diagnosisDecision: '',
  });
  const [isProcessingSSITakeover, setIsProcessingSSITakeover] = useState(false);

  // Non-blocking inline flag: does the currently-selected ADHD decision differ
  // from the outcome the in-clinic (Path 1) Post-Assessment Discussion was run
  // under? Shown quietly on the decision panel; the actionable redo/leave-as-is
  // choice is confirmed later, at DD-module completion (3_Formulation.js).
  const showPadMismatchInlineNote = shouldShowPadDdMismatch({
    ciFinishPath: wf.ciFinishPath,
    provisionalAdhdOutcome:
      patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.provisionalAdhdOutcome,
    diagnosisDecision: selections?.[5]?.[0],
  });

  const ssiOverrideActive = isSSILeadOverrideActive(patientDataModel);
  // True when Option 1 must call the takeover cloud function before the Lead
  // can edit SSI (override not yet active, Lead is not already the SSI clinician).
  const needsSSITakeoverFromDD =
    wf.allowLeadSSIOverride &&
    !ssiOverrideActive &&
    isLeadClinician(patientDataModel, auth.currentUser?.uid) &&
    auth.currentUser?.uid !== patientDataModel?.clinicians?.clinicianSSI?.clinicianId &&
    patientDataModel?.dataModel?.['3_semiStructuredInterview']?.status === 'completed';

  // The ADHD decision currently on record. Prefer the in-memory selection — it
  // is the live source of truth the rest of this page reads (handleSelection,
  // handleNavigationChoice, goesToMdtPlan) and it avoids a stale read of
  // patientDataModel immediately after a decision change (before the next
  // fetch). Falls back to the persisted value (identical on a fresh mount, where
  // selections are initialised from it).
  const currentDiagnosisDecision =
    selections?.[5]?.[0]
    || patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[currentSectionNumber]?.diagnosisDecision
    || null;

  // Single gate driving the SSI-decision-mismatch enforcement. True only for
  // SSI-mismatch-enabled orgs (SLaM / 3 Borough), for the editing Lead, when the
  // completed SSI counts do not support the chosen ADHD decision AND the Lead
  // has not resolved it (no active override, and no "keep as is" acknowledgement
  // for this exact decision). Always false for TOC / WLC / everyone else, so all
  // exit/confirm paths fall straight through to their existing behaviour.
  const hasUnresolvedMismatch = useMemo(() => {
    const ssiCompleted =
      patientDataModel?.dataModel?.['3_semiStructuredInterview']?.status === 'completed';
    if (!canEdit || !wf.allowLeadSSIOverride || isSSIResetMode || !ssiCompleted) {
      return false;
    }
    const ackDecision =
      patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[currentSectionNumber]?.ssiMismatchAcknowledgedDecision
      || null;
    return isUnresolvedSSIDdMismatch({
      diagnosisDecision: currentDiagnosisDecision,
      attentionDeficitData,
      hyperactivityImpulsivityData,
      domainImpairmentData,
      overrideActive: ssiOverrideActive,
      acknowledgedDecision: ackDecision,
    });
  }, [
    canEdit, wf.allowLeadSSIOverride, isSSIResetMode, ssiOverrideActive,
    currentDiagnosisDecision, attentionDeficitData, hyperactivityImpulsivityData,
    domainImpairmentData, patientDataModel,
  ]);

  const closeConsistencyDialog = () =>
    setSsiDdConsistencyDialog({ open: false, diagnosisDecision: '' });

  // Stashes the "what to do next" action while the mismatch dialog is open. Set
  // by attemptExit (below); run by a proceed-type resolution (Keep as is /
  // Finish DD first); cleared by Edit now / Go back.
  const pendingMismatchActionRef = useRef(null);

  // Central mismatch gate for every exit / confirm path. If there is an
  // unresolved mismatch, it stashes the path's own `proceed` action and opens
  // the dialog, returning true so the caller stops. Otherwise returns false and
  // the caller proceeds normally. Kept orthogonal to the save/complete flow:
  // `proceed` is always the path's exact current behaviour.
  const attemptExit = useCallback((proceed) => {
    if (!hasUnresolvedMismatch) return false;
    pendingMismatchActionRef.current = proceed || null;
    setSsiDdConsistencyDialog({ open: true, diagnosisDecision: currentDiagnosisDecision || '' });
    return true;
  }, [hasUnresolvedMismatch, currentDiagnosisDecision]);

  // Keep the interceptor's refs current (it is registered once and stored in a
  // ref, so it must read these via refs rather than a captured closure).
  attemptExitRef.current = attemptExit;
  hasChangesRef.current = hasChanges;

  // Safety-net hook (declared here, not at the top of the component, so it can
  // receive `forceDirty`). `forceDirty: hasUnresolvedMismatch` arms the nav
  // guard's dirty state so breadcrumb / top-nav exits delegate to our exit
  // interceptor (and `beforeunload` warns) even when there are no unsaved edits.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'CYP ADHD Diagnostic Decision',
    sectionTitle: 'Diagnostic Decision',
    hasChanges,
    canEdit,
    patientDataModel,
    forceDirty: hasUnresolvedMismatch,
  });

  // Grant the Lead edit access to the SSI by taking over the module — but only
  // when a takeover is actually needed (they are not already the effective SSI
  // owner and one is not already active). Idempotent-safe: if the module has
  // already been taken over (e.g. a race, or a prior takeover started from the
  // patient overview), we treat that as success rather than surfacing an error,
  // because edit access is effectively already granted.
  const runSSITakeoverIfNeeded = async () => {
    if (!needsSSITakeoverFromDD) return { ok: true };
    const result = await TriggerOverrideSSI(patientId);
    const outcome = shouldProceedAfterTakeover(result);
    // Refresh whenever we're going to proceed so the newly-active leadOverride
    // (and the Lead's SSI edit access) is reflected in the patient model.
    if (outcome.ok) {
      await fetchPatientData(patientId, true);
    }
    return outcome;
  };

  // Dialog: "Go back and change my decision" (mis-click recovery). No side
  // effects — the decision radio stays editable so the Lead can re-select, and
  // re-selecting re-runs the consistency check. Also used when the dialog is
  // dismissed via Escape.
  const handleGoBackToDecision = () => {
    if (isProcessingSSITakeover) return;
    // The clinician wants to reconsider their decision — abandon any stashed
    // exit action so we don't navigate away behind their back, and keep the
    // mismatch unresolved (it re-arms on the next exit attempt).
    pendingMismatchActionRef.current = null;
    closeConsistencyDialog();
  };

  // Dialog Step 1: "No — keep the SSI decisions". Records a lightweight
  // acknowledgement (governance parity with padMismatchAcknowledgedAt) so we
  // can see that a potential mismatch was surfaced and consciously accepted —
  // e.g. a legitimate "Partially met" where the symptoms are genuinely
  // borderline. Non-blocking; the acknowledgement write is audit-only and must
  // never stop the clinician proceeding.
  const handleKeepSSIAsIs = async () => {
    const acknowledgedDecision = ssiDdConsistencyDialog.diagnosisDecision || null;
    // Capture and clear the stashed exit action up-front so it runs exactly
    // once after the acknowledgement is recorded, regardless of any errors.
    const proceed = pendingMismatchActionRef.current;
    pendingMismatchActionRef.current = null;
    closeConsistencyDialog();
    try {
      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              ssiMismatchAcknowledgedAt: serverTimestamp(),
              ssiMismatchAcknowledgedBy: auth.currentUser?.uid || null,
              ssiMismatchAcknowledgedByName: auth.currentUser?.displayName || null,
              ssiMismatchAcknowledgedDecision: acknowledgedDecision,
            },
          },
        },
      });
      // Refresh so the acknowledgement is reflected in patientDataModel and the
      // mismatch gate (hasUnresolvedMismatch) clears for this decision. Without
      // this the dialog would re-arm on the next navigation even though the
      // clinician just accepted the SSI as-is.
      await fetchPatientData(patientId, true);
    } catch (err) {
      console.error('Failed to record SSI mismatch acknowledgement:', err);
    }
    // Continue whatever exit / confirm the clinician was attempting when the
    // dialog was raised (e.g. complete the DD, or navigate away). No-op when the
    // dialog was opened directly by picking a mismatching decision.
    if (proceed) proceed();
  };

  // Dialog Step 2 — "Edit the SSI now": grant edit access (if needed)
  // then open the Semi-Structured Interview. DD selections are already saved,
  // so the Lead can return to finish the Diagnostic Decision afterwards.
  const handleConfirmEditNow = async () => {
    // Going to edit the SSI now supersedes any stashed exit action (we navigate
    // to the SSI ourselves), so drop it.
    pendingMismatchActionRef.current = null;
    setIsProcessingSSITakeover(true);
    try {
      const { ok, message } = await runSSITakeoverIfNeeded();
      if (!ok) {
        toast.error(
          message ||
            'Could not open the Semi-Structured Interview for editing. Please try again.'
        );
        return;
      }
      closeConsistencyDialog();
      navigate(`/patient/${patientId}/proforma/cyp-adhd/semi-structured-interview/symptoms`);
    } catch (err) {
      toast.error(
        err.message ||
          'Could not open the Semi-Structured Interview for editing. Please try again.'
      );
    } finally {
      setIsProcessingSSITakeover(false);
    }
  };

  // Dialog Step 2 — "Finish the Diagnostic Decision first": grant edit access
  // (if needed) so the Lead can edit the SSI later from the Patient Overview,
  // then close and stay on the Diagnostic Decision.
  const handleConfirmFinishDDFirst = async () => {
    // Capture the stashed exit action; the takeover below marks the override
    // active, which resolves the mismatch, so this proceed can run without
    // re-triggering the dialog.
    const proceed = pendingMismatchActionRef.current;
    pendingMismatchActionRef.current = null;
    setIsProcessingSSITakeover(true);
    try {
      const takeoverWasNeeded = needsSSITakeoverFromDD;
      const { ok, message } = await runSSITakeoverIfNeeded();
      if (!ok) {
        toast.error(
          message ||
            'Could not grant editing access to the Semi-Structured Interview. Please try again.'
        );
        return;
      }
      if (takeoverWasNeeded) {
        toast.success(
          'You can update the Semi-Structured Interview later from the Patient Overview.'
        );
      }
      closeConsistencyDialog();
    } catch (err) {
      toast.error(
        err.message ||
          'Could not grant editing access to the Semi-Structured Interview. Please try again.'
      );
      return;
    } finally {
      setIsProcessingSSITakeover(false);
    }
    // Continue the original confirm / exit (e.g. open the DD completion dialog,
    // or navigate away) now that SSI edit access is granted for later.
    if (proceed) proceed();
  };

  // Check if current user is the Lead clinician (relevant for 3 Borough / SLaM conflict detection)
  const isCurrentUserLead = useMemo(() => {
    if (!patientDataModel || !auth.currentUser?.uid) return false;
    return isLeadClinician(patientDataModel, auth.currentUser.uid);
  }, [patientDataModel]);

  // Check if this organisation uses SSI-first workflow (3 Borough / SLaM)
  // Uses wf.isSSIFirst which is already set based on org detection
  const isSSIFirstOrg = useMemo(() => {
    return wf.isSSIFirst;
  }, [wf.isSSIFirst]);

  // Universal navigation handler for DD completion and routing.
  // Queues any final writes, flushes, and chains AI generation to the
  // flush Promise.  Module-completion transactions are still awaited.
  const handleNavigationChoice = async (choice) => {
    setIsNavigating(true);
    markRegenHandled();

    const diagnosisDecision = selections[5][0];

    // Queue clinicianNotes BEFORE flushing so they're included in the
    // same Firestore transaction as any other pending edits.
    // TOC v2 patients don't use the next steps text box (their next steps
    // are covered by the Final Formulation), so we skip writing clinicianNotes.
    if (diagnosisDecision === 'Not met' && nextSteps && nextSteps.trim() && !wf.hasMultiStepDD) {
      updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              clinicianNotes: nextSteps,
            },
          },
        },
      });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    // Chain AI generation to the flush — fires as soon as data is committed
    const generated = patientDataModel?.dataModel?.['6_manageReports']
      ?.sections?.['3']?.stageSectionsGenerated || [];
    const isGenerated = generated.includes('Diagnostic Decision');
    if (hasChanges || !isGenerated) {
      TriggerGenerateDocumentSection(patientId, 'CYP ADHD Diagnostic Decision', 'Diagnostic Decision', [], dataReady);
    }

    // For secondary diagnoses: navigate immediately (fire-and-forget)
    if (choice === 'secondary') {
      const csdRoute = wf.isFlatDifferentials
        ? `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/other-conditions`
        : `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/secondary-diagnoses`;
      navigate(csdRoute);
      return;
    }

    // TOC: navigate to Formulation page (the final DD step) without running
    // the module-completion transaction — that happens on the Formulation page.
    if (choice === 'formulation') {
      navigate(`/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/final-formulation`);
      return;
    }

    // Semble MDT Report orgs (WLC): ADHD-positive patients proceed to the MDT
    // Plan page (the final DD step) without running the module-completion
    // transaction — that happens on the MDT Plan page instead.
    if (choice === 'mdtPlan') {
      navigate(`/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/mdt-plan`);
      return;
    }

    try {
      // Await the flush to ensure queued data is committed before the
      // separate module-completion transaction runs (avoids contention)
      await dataReady;

      // Module completion — direct Firestore transaction on dot-notation
      // paths, separate from the debounce queue.
      const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
      const ddFirstCompletedFields = buildFirstCompletedFieldsDotNotation('4_diagnosticDecision', patientDataModel?.dataModel?.['4_diagnosticDecision']);
      const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['4_diagnosticDecision']);

      // Only unlock/lock PAD when still locked — never regress Path 1 PAD progress.
      const padStatusUpdate = resolvePadStatusOnDdComplete(
        patientDataModel,
        shouldShowAdhdFlow || wf.isSSIAlwaysExpected
      );

      // CYP: no monolithic patient dataModel exists, so the adult page's
      // direct dataModel.* transaction is bypassed — the write goes through
      // updatePatientFirestore, which the CYP DD bridge translates into the
      // modular stage documents.
      const completionUpdates = {
        '4_diagnosticDecision': {
          status: 'completed',
          progress: 100,
          docs_available: true,
          lastCompletedAt: serverTimestamp(),
          lastCompletedBy: auth.currentUser.uid,
          lastCompletedByName: auth.currentUser.displayName,
          ...firstCompletedFields,
        },
        '6_manageReports': {
          ddReportGenerated: true,
          ddReportGeneratedAt: serverTimestamp(),
          ddReportGeneratedBy: auth.currentUser.uid,
          ddReportGeneratedByName: auth.currentUser.displayName,
          ...buildFirstGeneratedFields('dd', manageReports),
        }
      };
      if (padStatusUpdate) {
        completionUpdates['5_postAssessmentDiscussion'] = { status: padStatusUpdate };
      }
      await updatePatientFirestore(patientId, completionUpdates);

      appendReportAuditEntry(patientId, {
        module: 'dd',
        eventType: 'generated',
        isFirstOccurrence: !manageReports?.ddReportFirstGeneratedAt,
      });

      // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
      recordInferredAppointmentDateOnCompletion({
        patientId,
        moduleKey: 'dd',
        didWriteFirstCompleted: Object.keys(ddFirstCompletedFields).length > 0,
        enabled: wf.isAppointmentDatesEnabled,
      });

      // Semble MDT Report orgs: this completion path is only reached for
      // non-ADHD outcomes (ADHD-positive patients complete on the MDT Plan
      // page instead). Fire the paragraph generation now that DD is complete
      // so the partial report (Sections 1-2, no Plan) is ready for review.
      if (false /* CYP: MDT Plan descoped (22 Aug) */) {
        TriggerSembleMdtReport({ clinicianId: auth.currentUser.uid, patientId });
      }

      if (choice === 'documents') {
        setIsDialogOpen(false);
        setIsGenerating(true);
      } else if (choice === 'pad') {
        setIsDialogOpen(false);
        navigate(`/patient/${patientId}/proforma/cyp-adhd/post-assessment-discussion/review-assessment`);
      }

    } catch (error) {
      console.error("Error during navigation:", error);
      setIsNavigating(false);
      alert("An error occurred. Please try again.");
    }
  };

  const handleGenerateReport = async () => {
    await handleNavigationChoice('documents');
  };
  
  const handleGoToSecondaryDiagnoses = async () => {
    await handleNavigationChoice('secondary');
  };

  const handleGoToFormulation = async () => {
    await handleNavigationChoice('formulation');
  };

  const handleGoToMdtPlan = async () => {
    await handleNavigationChoice('mdtPlan');
  };
  
  const handleGoToPostAssessmentDiscussion = async () => {
    await handleNavigationChoice('pad');
  };

  // "Save & Continue" handler for intercepted exits (breadcrumb, top nav,
  // sidebar). Triggers LLM regeneration for the DD section, marks the regen
  // as handled (so the unmount cleanup doesn't double-fire), clears the
  // pending destination, and navigates to wherever the clinician was headed.
  const handleSaveAndContinue = useCallback(async () => {
    if (!pendingExitRoute) return;
    setIsNavigating(true);
    markRegenHandled();

    const dataReady = flushPendingUpdates();

    const generated = patientDataModel?.dataModel?.['6_manageReports']
      ?.sections?.['3']?.stageSectionsGenerated || [];
    const isGenerated = generated.includes('Diagnostic Decision');
    if (hasChanges || !isGenerated) {
      TriggerGenerateDocumentSection(patientId, 'CYP ADHD Diagnostic Decision', 'Diagnostic Decision', [], dataReady);
    }

    const destination = pendingExitRoute;
    const exitCallback = pendingExitCallbackRef.current;
    setPendingExitRoute(null);
    pendingExitCallbackRef.current = null;
    setIsDialogOpen(false);

    if (exitCallback) {
      await exitCallback();
    } else {
      navigate(destination);
    }
  }, [pendingExitRoute, patientId, patientDataModel, hasChanges, navigate, flushPendingUpdates, markRegenHandled]);

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  // SSI Conflict Alert handlers (3 Borough / SLaM workflow)
  
  /**
   * Handle closing the conflict alert
   * If closed without action, show the normal completion dialog
   */
  const handleConflictAlertClose = useCallback(() => {
    setIsConflictAlertOpen(false);
    setConflictDetails(null);
  }, []);

  /**
   * Handle viewing the SSI report from the conflict alert
   * Opens the embedded SSI report dialog (same as supporting documents panel)
   */
  const handleViewSSIReport = useCallback(() => {
    console.log('View SSI Report clicked - opening embedded dialog');
    handleOpenDocumentDialog("Semi-Structured Interview");
  }, []);

  /**
   * Handle resetting the SSI section for re-editing
   * This triggers the cloud function to archive and reset SSI data,
   * then proceeds to the normal DD completion dialog. SSI editing will be done later.
   */
  const handleResetSSI = useCallback(async () => {
    setIsResettingSSI(true);
    try {
      console.log('Reset SSI requested with conflict details:', conflictDetails);
      
      // Call the resetSSIForLeadClinician cloud function
      const result = await TriggerResetSSI(patientId, conflictDetails);
      
      if (result.success) {
        console.log('SSI Reset successful:', result);
        
        // Refresh patient data to get the updated state
        await fetchPatientData(patientId);
        
        // Close the conflict alert
        setIsConflictAlertOpen(false);
        setConflictDetails(null);
        
        // Proceed to the normal DD completion dialog (SSI editing will be done later)
        setIsDialogOpen(true);
      } else {
        console.error('SSI Reset failed:', result.message);
        alert(`Failed to reset SSI: ${result.message}`);
      }
    } catch (error) {
      console.error('Error resetting SSI:', error);
      alert('An error occurred while resetting SSI. Please try again.');
    } finally {
      setIsResettingSSI(false);
    }
  }, [conflictDetails, patientId, fetchPatientData]);

  // Separate validation for dialog buttons — checks if next steps are
  // required and filled.  Uses shouldShowAdhdFlow (not isAdhdAfterCI) so
  // that flat-model and deferred-DF patients are correctly required to
  // provide next steps when the ADHD criteria outcome is "Not met".
  // TOC v2 patients skip this requirement — their next steps are covered
  // by the Final Formulation paragraph instead.
  const isDialogFormValid = useCallback(() => {
    if (shouldShowAdhdFlow) {
      const diagnosisDecision = selections[5][0];
      if (diagnosisDecision === 'Not met' && !wf.hasMultiStepDD) {
        return nextSteps && nextSteps.trim() !== '';
      }
    }
    // Hierarchical non-ADHD cases or positive ADHD — dialog form always valid
    return true;
  }, [selections, nextSteps, shouldShowAdhdFlow, wf.hasMultiStepDD]);
  
  // Whether to show the "Generate Report" button in the completion dialog.
  // Shown whenever DD is a terminal step (no secondary diagnoses left to navigate to),
  // giving ALL organisations the option to generate the report and proceed to review.
  const shouldShowGenerateReportButton = useCallback(() => {
    return !hasOtherConditions;
  }, [hasOtherConditions]);

  // Whether to show the "Go to Post-Assessment Discussion" button.
  // PAD is part of the workflow when either the ADHD assessment path was
  // taken (hierarchical: PPD = ADHD) OR the org always conducts a full
  // assessment (always-SSI orgs like WLC, TOC, SLaM).  For always-SSI
  // orgs, PAD always happens — the DD ADHD outcome only determines
  // full vs partial PAD content.  The button is hidden when there are
  // remaining conditions to handle (go to Other Conditions first).
  const shouldShowPADButton = useCallback(() => {
    return (shouldShowAdhdFlow || wf.isSSIAlwaysExpected) && !hasOtherConditions;
  }, [shouldShowAdhdFlow, wf.isSSIAlwaysExpected, hasOtherConditions]);

  const handleNextPageButtonClick = (route) => {
    navigate(route);
  };

  const constructDiagnosisSummary = useCallback(() => {
    let output = [];
    
    if (!shouldShowAdhdFlow) {
      // Hierarchical non-ADHD path: show the non-ADHD diagnosis decision
      // using the PPD from CI section 12.  This branch is never reached
      // for flat-model or deferred-DF orgs (shouldShowAdhdFlow is always
      // true for them).
      const nonAdhdData = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[1] || {};
      const provisionalPrimaryDiagnosis = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[12]?.provisionalPrimaryDiagnosis || 'Not specified';
      
      output.push(`• Provisional Primary Diagnosis: ${provisionalPrimaryDiagnosis}`);
      
      if (nonAdhdData.nonAdhdDiagnosisDecision) {
        output.push(`• Diagnosis Confirmed: ${nonAdhdData.nonAdhdDiagnosisDecision}`);
        
        if (nonAdhdData.nonAdhdDiagnosisNotes) {
          output.push(`• Your Notes: ${nonAdhdData.nonAdhdDiagnosisNotes}`);
        }
      }
      
      return output.map((bullet, index) => (
        <div key={index} className="mb-1">
          {bullet}
        </div>
      ));
    }
    
    // For ADHD cases, show the ADHD decision summary
    const diagnosisDecision = selections[5][0];

    output.push(`• DSM-5 Criteria for ADHD: ${diagnosisDecision || 'Not specified'}`);

    if (diagnosisDecision === 'Fully met') {
      if (selections[5][1]) output.push(`• Presentation: ${selections[5][1]}`);
      if (selections[5][2]) output.push(`• Severity: ${selections[5][2]}`);
    } else if (diagnosisDecision === 'Partially met') {
      if (selections[5][3]) output.push(`• Classification: ${selections[5][3]}`);
      if (selections[5][3] === 'Other Specified ADHD' && selections[5][4]) {
        const reason = selections[5][4] === 'Other' && selections[5][5]?.trim()
          ? `Other - ${selections[5][5].trim()}`
          : selections[5][4];
        output.push(`• Reason: ${reason}`);
      }
    }
    if (adhdCriteriaMet(diagnosisDecision) && Array.isArray(selections[5][6]) && selections[5][6].length > 0) {
      output.push(`• Supporting Evidence: ${selections[5][6].join(', ')}`);
    }

    return output.map((bullet, index) => (
      <div key={index} className="mb-1">
        {bullet}
      </div>
    ));
  }, [selections, shouldShowAdhdFlow, patientDataModel]);

  // const handleRecording = () => {
  //   if (!('webkitSpeechRecognition' in window)) {
  //     alert('Speech recognition not supported in this browser.');
  //     return;
  //   }

  //   if (recognition) {
  //     recognition.stop();
  //     return;
  //   }

  //   const newRecognition = new window.webkitSpeechRecognition();
  //   newRecognition.continuous = true;
  //   newRecognition.interimResults = true;
  //   newRecognition.lang = 'en-GB';

  //   newRecognition.onresult = (event) => {
  //     let finalTranscript = nextSteps;
  //     for (let i = event.resultIndex; i < event.results.length; i++) {
  //       let transcript = event.results[i][0].transcript.trim();
  //       if (event.results[i].isFinal) {
  //         transcript = ' ' + transcript.charAt(0).toUpperCase() + transcript.slice(1) + '.';
  //         finalTranscript += transcript;
  //       }
  //     }
  //     setNextSteps(finalTranscript);
  //   };

  //   newRecognition.onend = () => {
  //     setActiveRecording(false);
  //     setRecognition(null);

  //     // Simulate focus and blur to trigger onBlur
  //     if (textAreaRef.current) {
  //       textAreaRef.current.focus();
  //       setTimeout(() => {
  //         textAreaRef.current.blur();
  //       }, 0);
  //     }
  //   };

  //   newRecognition.start();
  //   setRecognition(newRecognition);
  //   setActiveRecording(true);
  // };

  const sendSectionUpdates = useCallback((newUpdates) => {
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore]);

  // Add these effects and handlers
  useEffect(() => {
    // Load saved notes from localStorage or Firestore if needed
    const savedNotes = localStorage.getItem(`nextSteps_${patientId}`);
    if (savedNotes) {
      setNextSteps(savedNotes);
    }
  }, [patientId]);

  const handleNotesChange = useCallback((newNotes) => {
    if (!canEdit) return;
    
    setNextSteps(newNotes);
    localStorage.setItem(`nextSteps_${patientId}`, newNotes);
    setHasChanges(true);
  }, [patientId, canEdit]);

  const handleNotesBlur = () => {
    if (!canEdit) return;
    
    sendSectionUpdates({ clinicianNotes: nextSteps });
    setHasChanges(true);
  };

  // Core "confirm / complete the Diagnostic Decision" flow: saves selections,
  // marks the section complete, and opens the completion dialog (or SSI-conflict
  // alert). Deliberately free of any SSI-decision-mismatch logic so it can be
  // used both as a raw "proceed" action stashed by the mismatch dialog and as
  // the exit interceptor's save target. The mismatch gate is applied by callers
  // (Confirm buttons / attemptExit), never here.
  const handleFinishDDCore = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);

    // NOTE: building `updates` is done INSIDE the try block on purpose. If a
    // clinician clicks "Confirm ... Diagnostic Decision" while `selections` and
    // `questionPanels` are momentarily out of sync (e.g. clicking around quickly
    // before state has settled), `selections[panelIndex][questionIndex]` can
    // throw. Previously this loop ran after setIsSaving(true) but BEFORE the
    // try, so the throw was uncaught and isSaving was never reset — leaving the
    // forward CTA permanently stuck in the disabled grey "Saving Progress..."
    // state with no way to recover. Keeping it inside the try guarantees the
    // catch resets isSaving and clears any pending exit state.
    try {
      const updates = {};

      // Iterate over the questionPanels and selections to prepare updates
      questionPanels.forEach((panel, panelIndex) => {
        panel.questions.forEach((question, questionIndex) => {
          const value = selections[panelIndex]?.[questionIndex];
          updates[question.key] = value;
        });
      });

      // Save form data to Firestore
      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              ...updates,
              status: 'completed',
              progress: 100,
            },
          },
        },
      });
  
      await fetchPatientData(patientId);        
      setIsSaving(false);

      // 3 Borough / SLaM workflow: Check for SSI conflict before showing the completion dialog.
      // We perform a fresh Firestore read rather than using patientDataModel from
      // the useCallback closure because React state updates (from fetchPatientData)
      // are asynchronous — the closure still holds the pre-save snapshot where
      // DD status is not yet 'completed', causing isDDCompleted() to return false
      // and the DD-stage conflict branch to never fire.
      if (isSSIFirstOrg && isCurrentUserLead) {
        const freshSnap = await getDoc(doc(db, 'patients', patientId));
        const freshData = freshSnap.data();
        const conflict = detectSSIConflict(freshData, claims?.organisation);
        
        if (conflict.hasConflict && conflict.shouldShowConflictUI) {
          console.log('🔍 DD: SSI Conflict detected:', conflict);
          setConflictDetails(conflict);
          setIsConflictAlertOpen(true);
          return;
        }
      }

      // ALWAYS show dialog - all logic will be handled in dialog buttons
      setIsDialogOpen(true);
      
    } catch (error) {
      console.error("Failed to update Firestore before navigation:", error);
      setIsSaving(false);
      setPendingExitRoute(null);
      pendingExitCallbackRef.current = null;
    }
  }, [isSaving, patientId, fetchPatientData, selections, updatePatientFirestore, questionPanels, isSSIFirstOrg, isCurrentUserLead, claims?.organisation]);

  // Keep the ref in sync so the exit interceptor / sidenav can call the core
  // completion flow without a stale closure. It points at the mismatch-free
  // core: those paths run their own attemptExit gate before invoking it.
  handleFinishDDRef.current = handleFinishDDCore;

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}/cyp-adhd`],
    [wf.isFlatDifferentials ? "Decision for ADHD" : "Diagnostic Decision"]
  ];

  // Debug state changes
  useEffect(() => {
    console.log('🔍 DD: State Debug - wf.isSSIFirst:', wf.isSSIFirst, 'isAdhdAfterCI:', isAdhdAfterCI, 'hasRecommendedDiagnosis:', hasRecommendedDiagnosis);
  }, [wf.isSSIFirst, isAdhdAfterCI, hasRecommendedDiagnosis]);

  // Criterion E only ever shows a value the clinician saved. If nothing is
  // saved yet, make sure the page shows it blank — a stale value could
  // otherwise sit in local state after the page's data refreshes.
  //
  // The userSetCriterionERef guard protects a click made in the window
  // before the debounced Firestore write lands.
  useEffect(() => {
    if (userSetCriterionERef.current) return;
    if (questionPanels.length === 0) return;

    const criterionEPanelIndex = 4;
    const criterionEQuestionIndex = 0;

    if (questionPanels[criterionEPanelIndex]?.questions[criterionEQuestionIndex]?.key !== 'dsmCriterionE_NotOtherExplanation') return;

    const currentSelection = selections[criterionEPanelIndex]?.[criterionEQuestionIndex];
    const firebaseValue = finalDiagnosisSection?.dsmCriterionE_NotOtherExplanation;

    if (firebaseValue !== undefined && firebaseValue !== null) return;

    if (currentSelection !== null && currentSelection !== undefined) {
      const newSelections = selections.map(panel => [...panel]);
      newSelections[criterionEPanelIndex][criterionEQuestionIndex] = null;
      setSelections(newSelections);
    }
  }, [questionPanels, selections, finalDiagnosisSection]);

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      {/* Multi-step DD patients get a sidebar navigation; all other patients keep
          the existing single-column layout with no structural changes. */}
      <div className={wf.hasMultiStepDD ? 'flex items-start' : ''}>
        {wf.hasMultiStepDD && ddNavSections && (
          <DDSideNavbar
            sections={ddNavSections}
            currentSectionIndex={1}
            navigateToSection={navigateToDDSection}
          />
        )}
      <div className="flex-grow p-8">
        {/* Preview mode info banner — shown when opened via a Preview button */}
        {isPreviewMode && <PreviewModeBanner />}
        <div className="flex justify-between items-center mb-3">

          {/* Left column: Back button.
              TOC patients came from MDT Review, so we send them back there.
              All other patients return to Patient Overview. */}
          <div className="w-1/3 flex justify-start">
          <button
            onClick={() => {
              const backRoute = wf.hasMultiStepDD
                ? `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/mdt-review`
                : `/patient/${patientId}`;
              // Existing dirty-aware back behaviour, gated behind the SSI
              // decision mismatch dialog first (no-op for non-mismatch orgs).
              const proceed = () => {
                if (hasChanges && canEdit && handleFinishDDRef.current) {
                  setPendingExitRoute(backRoute);
                  handleFinishDDRef.current();
                  return;
                }
                flushPendingUpdates();
                navigate(backRoute);
              };
              if (attemptExit(proceed)) return;
              proceed();
            }}
            className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
            <span className="font-bold">{wf.hasMultiStepDD ? ((wf.isMDTReview || wf.isTOCWorkflow) ? 'Back to MDT Review' : 'Back to Review') : 'Back to Patient Overview'}</span>
          </button>
          </div>

          {/* Centered heading with tooltip */}
          <div className="w-1/3 flex justify-center items-center">
            <h1 className="text-2xl font-bold">{wf.isFlatDifferentials ? "Decision for ADHD" : "Diagnostic Decision"}</h1>
          </div>

          {/* Forward button */}
          <div className="w-1/3 flex justify-end">
          {canEdit ? (
            <button
              onClick={() => { if (!attemptExit(handleFinishDDCore)) handleFinishDDCore(); }}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${!isFormValid() || isSaving
                ? (isSaving ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                : 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
                }`}
              disabled={!isFormValid() || isSaving}
            >
              <span className="font-bold">{isSaving ? "Saving Progress..." : (wf.isFlatDifferentials ? "Confirm ADHD Diagnostic Decision" : "Confirm Primary Diagnostic Decision")}</span>
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
            </button>
          ) : wf.hasMultiStepDD && (
            <button
              onClick={() => navigate(hasOtherConditions
                ? `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/other-conditions`
                : `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/final-formulation`
              )}
              className="flex items-center py-2 px-4 rounded-md shadow-md bg-violet-900 hover:bg-violet-800 text-white font-bold transition-all duration-300"
            >
              <span>{hasOtherConditions ? `Next: ${getFlatOtherConditionsStepTitle(wf.isTOCWorkflow)}` : 'Next: Formulation'}</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          )}
          </div>
        </div>


        {shouldShowAdhdFlow ? (
        <div className="flex space-x-4">        

          {/* SSI Symptom & Domain Tables - Hidden when in SSI Reset Mode */}
          {!isSSIResetMode && (
          <div className="w-3/5 rounded-2xl space-y-6">
            {/* Criterion A: Symptom summary table
                Column widths use fixed percentages so the adulthood/childhood
                columns align vertically with the domains table below.
                Criterion (10%) + Symptom (50%) = 60%, Adulthood 20%, Childhood 20%. */}
            <table className="w-full text-left shadow-lg rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '50%' }} />
                <col style={{ width: mergeOnsetColumns ? '40%' : '20%' }} />
                {!mergeOnsetColumns && <col style={{ width: '20%' }} />}
              </colgroup>
              <thead>
                <tr className="bg-primary text-white">
                  <th className="p-3 text-center">Criterion DSM-5</th>
                  <th className="p-3">Symptom</th>
                  <th className="p-3 text-center">
                    {mergeOnsetColumns
                      ? 'Present for 6 months or more and before age 12'
                      : 'Present in the last 6 months'}
                  </th>
                  {!mergeOnsetColumns && <th className="p-3 text-center">Present before age 12</th>}
                </tr>
              </thead>
              <tbody>
                {attentionDeficitData.map((item, index) => (
                  <tr
                    key={index}
                    className={`bg-primary/5 border-b border-primary/10 ${wf.hasMultiStepDD ? 'group cursor-pointer hover:bg-primary/10 transition-colors' : ''}`}
                    onClick={wf.hasMultiStepDD ? () => setActiveSymptomPopup(buildSymptomPopupData(item)) : undefined}
                  >
                    <td className="p-3 text-sm text-center font-bold text-primary">{item.criterion}</td>
                    <td className="p-3 text-xs">{item.symptom}</td>
                    <td className="p-3 text-center bg-white group-hover:bg-gray-100 transition-colors">
                      <SsiDecisionCell value={item.adulthood} />
                    </td>
                    {!mergeOnsetColumns && (
                      <td className="p-3 text-center bg-white group-hover:bg-gray-100 transition-colors">
                        <SsiDecisionCell value={item.childhood} />
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="bg-primary/15 font-bold text-primary">
                  <td className="p-3" colSpan="2">Total criteria: Attention Deficit</td>
                  <td className="p-3 text-center">{attentionTotals.adulthoodTotal} / 9</td>
                  {!mergeOnsetColumns && (
                    <td className="p-3 text-center">{attentionTotals.childhoodTotal} / 9</td>
                  )}
                </tr>
                {hyperactivityImpulsivityData.map((item, index) => (
                  <tr
                    key={index}
                    className={`bg-primary/5 border-b border-primary/10 ${wf.hasMultiStepDD ? 'group cursor-pointer hover:bg-primary/10 transition-colors' : ''}`}
                    onClick={wf.hasMultiStepDD ? () => setActiveSymptomPopup(buildSymptomPopupData(item)) : undefined}
                  >
                    <td className="p-3 text-sm text-center font-bold text-primary">{item.criterion}</td>
                    <td className="p-3 text-xs">{item.symptom}</td>
                    <td className="p-3 text-center bg-white group-hover:bg-gray-100 transition-colors">
                      <SsiDecisionCell value={item.adulthood} />
                    </td>
                    {!mergeOnsetColumns && (
                      <td className="p-3 text-center bg-white group-hover:bg-gray-100 transition-colors">
                        <SsiDecisionCell value={item.childhood} />
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="bg-primary/15 font-bold text-primary">
                  <td className="p-3" colSpan="2">Total criteria: Hyperactivity / Impulsivity</td>
                  <td className="p-3 text-center">{hyperactivityTotals.adulthoodTotal} / 9</td>
                  {!mergeOnsetColumns && (
                    <td className="p-3 text-center">{hyperactivityTotals.childhoodTotal} / 9</td>
                  )}
                </tr>
              </tbody>
            </table>

            {/* Criterion D: Domains of Functional Impairment summary
                Domain name column spans the same 60% as Criterion + Symptom above,
                so the adulthood (20%) and childhood (20%) columns are vertically aligned. */}
            {domainImpairmentData.length > 0 && (
              <table className="w-full text-left shadow-lg rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '60%' }} />
                  <col style={{ width: mergeOnsetColumns ? '40%' : '20%' }} />
                  {!mergeOnsetColumns && <col style={{ width: '20%' }} />}
                </colgroup>
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="p-3">Domain of Functional Impairment</th>
                    <th className="p-3 text-center">Impaired in the last 6 months</th>
                    {!mergeOnsetColumns && <th className="p-3 text-center">Impaired before age 12</th>}
                  </tr>
                </thead>
                <tbody>
                  {domainImpairmentData.map((item, index) => (
                    <tr
                      key={index}
                      className={`bg-primary/5 border-b border-primary/10 ${wf.hasMultiStepDD ? 'group cursor-pointer hover:bg-primary/10 transition-colors' : ''}`}
                      onClick={wf.hasMultiStepDD ? () => setActiveSymptomPopup(buildDomainPopupData(item)) : undefined}
                    >
                      <td className="p-3 text-sm font-semibold text-primary">{item.domain}</td>
                      <td className="p-3 text-center bg-white group-hover:bg-gray-100 transition-colors">
                        <SsiDecisionCell value={item.adulthood} />
                      </td>
                      {!mergeOnsetColumns && (
                        <td className="p-3 text-center bg-white group-hover:bg-gray-100 transition-colors">
                          <SsiDecisionCell value={item.childhood} />
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr className="bg-primary/15 font-bold text-primary">
                    <td className="p-3">Total domains impaired</td>
                    <td className="p-3 text-center">{domainImpairmentData.filter(d => d.adulthood).length} / {domainImpairmentData.length}</td>
                    {!mergeOnsetColumns && (
                      <td className="p-3 text-center">{domainImpairmentData.filter(d => d.childhood).length} / {domainImpairmentData.length}</td>
                    )}
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* DSM Criteria Panel - Half width and centered when in SSI Reset Mode, w-1/2 otherwise */}
          <div className={`${isSSIResetMode ? 'w-1/2 mx-auto' : 'w-1/2'} bg-gray-50 rounded-2xl border border-gray-200 p-6 shadow-md`}>
            {questionPanels.map((panel, panelIndex) => (
              <div
                key={panelIndex}
                className={`${panelIndex > 0 ? 'border-t border-gray-200' : ''} py-2`}
              >
                <div className="flex items-center mb-2 gap-2">
                  <h4 className="font-semibold text-lg text-left">{panel.title}</h4>
                  {panel.tooltip && (
                    <InfoTooltip 
                      content={panel.tooltip} 
                      label={`Information about ${panel.title}`}
                      maxWidth="300px"
                      className="flex-shrink-0"
                    />
                  )}
                </div>

                {/* Non-blocking flag: the selected ADHD decision differs from the
                    outcome the in-clinic Post-Assessment Discussion was run under.
                    Shown quietly on the decision panel; the redo/leave-as-is choice
                    is confirmed at DD-module completion. */}
                {panelIndex === 5 && showPadMismatchInlineNote && (
                  <div className="mt-1 mb-2">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-left">
                      <div className="flex items-start">
                        <ExclamationIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600" aria-hidden="true" />
                        <p className="ml-3 text-sm text-amber-900">
                          Note: this differs from the outcome discussed in the Post-Assessment
                          Discussion. You'll confirm how to handle this when you complete the
                          Diagnostic Decision.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {panelIndex === 4 && (
                  <div className="mt-1 mb-2">
                    <div className="rounded-2xl p-4 text-left" style={{ backgroundColor: 'rgba(67, 33, 62, 0.08)' }}>
                      <div className="flex items-start">
                        <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#43213e' }} />
                        <p className="ml-3 text-sm" style={{ color: '#43213e' }}>
                          Criterion E is not pre-selected. Please use your clinical judgement to decide whether the symptoms are better explained by another psychiatric disorder, substance, or medication. A selection is required before you can confirm the Decision for ADHD.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {panel.questions.map((question, questionIndex) => {
                  // First determine if this question should be shown based on its showWhen condition
                  if (question.showWhen && !question.showWhen(selections)) {
                    return null; // Don't render this question at all if showWhen returns false
                  }

                  return (
                    <div key={questionIndex} className={`flex mb-2 text-left`}>
                      <div className={`${(question.radio_options || question.checklist_options || question.isTextInput) ? 'w-1/4 mt-4' : 'w-11/12'} text-sm pr-2 flex flex-col gap-1`}>
                        <div className="flex items-start">
                          <span>{question.name}</span>
                          {question.tooltip && (
                            <InfoTooltip 
                              content={question.tooltip}
                              label="Question information"
                              className="flex-shrink-0 ml-1"
                            />
                          )}
                        </div>
                        {/* Show instruction as subtitle if it exists */}
                        {question.subtitle && (
                          <span className="text-xs text-gray-600 italic">
                            {question.subtitle}
                          </span>
                        )}
                      </div>

                      {/* Question inputs section */}
                      <div className={`flex flex-col ${(question.radio_options || question.checklist_options || question.isTextInput) ? 'w-full mt-4' : ''}`}>
                        {question.isTextInput ? (
                          <div className={`w-full ${hasRecommendedDiagnosis && question.recommendedSelection ? 'border-violet-900 border-2 border-dashed rounded-lg p-0.5' : ''
                            }`}
                            {...(hasRecommendedDiagnosis && question.recommendedSelection && {
                              'data-tooltip-id': `recommendation-text-${panelIndex}-${questionIndex}`,
                              'data-tooltip-content': `${recommendedByName} recommended: "${question.recommendedSelection}"`
                            })}
                          >
                            <textarea
                              ref={(el) => { if (el) { el.style.height = '0px'; el.style.height = el.scrollHeight + 'px'; } }}
                              value={selections[panelIndex][questionIndex] || question.recommendedSelection || ''}
                              onChange={(e) => handleSelection(panelIndex, questionIndex, e.target.value)}
                              className={`w-full p-2 border border-gray-300 rounded-md ${!canEdit ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
                              style={{ overflow: 'hidden', resize: 'none' }}
                              rows={3}
                              placeholder={canEdit ? "Please specify..." : "Notes can only be added by the assigned clinician"}
                              onInput={(e) => { e.target.style.height = '0px'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                              disabled={!canEdit}
                            />
                            {hasRecommendedDiagnosis && question.recommendedSelection && (
                              <Tooltip
                                id={`recommendation-text-${panelIndex}-${questionIndex}`}
                                place="top"
                                style={{
                                  maxWidth: '300px',
                                  whiteSpace: 'normal',
                                  backgroundColor: 'rgba(34, 34, 34, 1)',
                                  borderRadius: '15px',
                                  color: '#fff',
                                  fontSize: '0.8em'
                                }}
                              />
                            )}
                          </div>
                        ) : question.radio_options ? (
                          question.radio_options.map((option, optionIndex) => {
                            const optionValue = typeof option === 'object' ? option.value : option;
                            const optionTooltip = typeof option === 'object' ? option.tooltip : null;

                            // Check if this option is the recommended one
                            const isRecommended = question.recommendedSelection === optionValue;

                            return (
                              <div
                                key={optionIndex}
                                className={`flex items-center justify-between px-2 py-1 rounded-lg transition-all duration-200 
                                ${selections[panelIndex][questionIndex] === optionValue
                                    ? 'bg-green-200 border-gray-600 border-2'
                                    : 'border border-transparent'
                                  }
                                ${hasRecommendedDiagnosis && isRecommended
                                    ? 'border-violet-900 border-2 border-dashed'
                                    : ''
                                  }
                                ${!canEdit ? 'opacity-75' : ''}`}
                                {...(hasRecommendedDiagnosis && isRecommended && {
                                  'data-tooltip-id': `recommendation-${panelIndex}-${questionIndex}-${optionIndex}`,
                                  'data-tooltip-content': `${recommendedByName} recommended this option in the Recommended Diagnosis section`
                                })}
                              >
                                <label className={`flex items-center text-sm grow ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                  <input
                                    type="radio"
                                    name={question.id || `question-${panelIndex}-${questionIndex}`}
                                    value={optionValue}
                                    checked={selections[panelIndex][questionIndex] === optionValue}
                                    onChange={() => handleSelection(panelIndex, questionIndex, optionValue)}
                                    className="mr-2 accent-green-600"
                                    disabled={!canEdit}
                                  />
                                  <div className="flex items-center">
                                    <span>{optionValue}</span>
                                    {optionTooltip && (
                                      <InfoTooltip 
                                        content={optionTooltip}
                                        label={`Information about option ${optionValue}`}
                                        side="right"
                                        className="flex-shrink-0 ml-1"
                                      />
                                    )}
                                  </div>
                                </label>
                                {canEdit && selections[panelIndex][questionIndex] === optionValue && (
                                  <button
                                    onClick={() => handleSelection(panelIndex, questionIndex, null)}
                                    className="text-gray-500 hover:text-gray-700 text-sm ml-2"
                                    aria-label="Clear selection"
                                  >
                                    <XIcon className="w-4 h-4" />
                                  </button>
                                )}
                                {hasRecommendedDiagnosis && isRecommended && (
                                  <Tooltip
                                    id={`recommendation-${panelIndex}-${questionIndex}-${optionIndex}`}
                                    place="top"
                                    style={{
                                      maxWidth: '300px',
                                      whiteSpace: 'normal',
                                      backgroundColor: 'rgba(34, 34, 34, 1)',
                                      borderRadius: '15px',
                                      color: '#fff',
                                      fontSize: '0.8em'
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })
                        ) : question.checklist_options ? (
                          <div className="space-y-1">
                            {question.checklist_options.map((option, optionIndex) => {
                              const isNoneAvailable = option.value === 'None available';
                              const currentSelections = Array.isArray(selections[panelIndex][questionIndex])
                                ? selections[panelIndex][questionIndex]
                                : [];
                              const isSelected = currentSelections.includes(option.value);
                              const isDisabledByNone = !isNoneAvailable && currentSelections.includes('None available');
                              const isDisabledByOthers = isNoneAvailable && currentSelections.length > 0 && !isSelected;

                              // Check if this option is recommended
                              const isRecommended = Array.isArray(question.recommendedSelection) &&
                                question.recommendedSelection.includes(option.value);

                              return (
                                <div
                                  key={optionIndex}
                                  className={`
                                  flex items-center p-1 rounded-lg transition-all duration-200
                                  ${isSelected ? 'bg-green-200 border-gray-600 border-2' : 'border border-transparent'}
                                  ${(isDisabledByNone || isDisabledByOthers || !canEdit) ? 'opacity-50' : ''}
                                  ${hasRecommendedDiagnosis && isRecommended ? 'border-violet-900 border-2 border-dashed' : ''}
                                `}
                                  {...(hasRecommendedDiagnosis && isRecommended && {
                                    'data-tooltip-id': `recommendation-checklist-${panelIndex}-${questionIndex}-${optionIndex}`,
                                    'data-tooltip-content': `${recommendedByName} recommended this option in the Recommended Diagnosis section`
                                  })}
                                >
                                  <label className={`flex items-center text-sm grow ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      disabled={isDisabledByNone || isDisabledByOthers || !canEdit}
                                      onChange={() => {
                                        let newSelections = [...currentSelections];

                                        if (isNoneAvailable) {
                                          // If "None available" is clicked
                                          newSelections = isSelected ? [] : ['None available'];
                                        } else {
                                          // For other options
                                          if (isSelected) {
                                            // Remove if already selected
                                            newSelections = newSelections.filter(s => s !== option.value);
                                          } else {
                                            // Add new selection and remove "None available" if present
                                            newSelections = newSelections
                                              .filter(s => s !== 'None available')
                                              .concat(option.value);
                                          }
                                        }

                                        handleSelection(panelIndex, questionIndex, newSelections);
                                      }}
                                      className="mr-2 accent-green-600 h-3 w-3"
                                    />
                                    <span>{option.value}</span>
                                  </label>
                                  {hasRecommendedDiagnosis && isRecommended && (
                                    <Tooltip
                                      id={`recommendation-checklist-${panelIndex}-${questionIndex}-${optionIndex}`}
                                      place="top"
                                      style={{
                                        maxWidth: '300px',
                                        whiteSpace: 'normal',
                                        backgroundColor: 'rgba(34, 34, 34, 1)',
                                        borderRadius: '15px',
                                        color: '#fff',
                                        fontSize: '0.8em'
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleSelection(panelIndex, questionIndex, true)}
                              className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                              ${selections[panelIndex][questionIndex] === true ? 'bg-green-200 border-gray-600 border-2' : 'bg-white border-gray-300'} 
                              ${hasRecommendedDiagnosis && question.recommendedSelection === true ? 'border-violet-900 border-2 border-dashed' : ''}
                              transition-all duration-300 ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''}`}
                              disabled={!canEdit}
                              {...(hasRecommendedDiagnosis && question.recommendedSelection === true && {
                                'data-tooltip-id': `recommendation-${panelIndex}-${questionIndex}-yes`,
                                'data-tooltip-content': `${recommendedByName} recommended this option in the Recommended Diagnosis section`
                              })}
                            >
                              <div className="px-0.5 justify-center items-center flex">
                                <div className="text-slate-700 text-sm font-semibold leading-tight">Yes</div>
                              </div>
                            </button>
                            <button
                              onClick={() => handleSelection(panelIndex, questionIndex, false)}
                              className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                              ${selections[panelIndex][questionIndex] === false ? 'bg-red-200 border-gray-600 border-2' : 'bg-white'} 
                              ${hasRecommendedDiagnosis && question.recommendedSelection === false ? 'border-violet-900 border-2 border-dashed' : ''}
                              transition-all duration-300 ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''}`}
                              disabled={!canEdit}
                              {...(hasRecommendedDiagnosis && question.recommendedSelection === false && {
                                'data-tooltip-id': `recommendation-${panelIndex}-${questionIndex}-no`,
                                'data-tooltip-content': `${recommendedByName} recommended this option in the Recommended Diagnosis section`
                              })}
                            >
                              <div className="px-0.5 justify-center items-center flex">
                                <div className="text-slate-700 text-sm font-semibold leading-tight">No</div>
                              </div>
                            </button>
                            {hasRecommendedDiagnosis && question.recommendedSelection === true && (
                              <Tooltip
                                id={`recommendation-${panelIndex}-${questionIndex}-yes`}
                                place="top"
                                style={{
                                  maxWidth: '300px',
                                  whiteSpace: 'normal',
                                  backgroundColor: 'rgba(34, 34, 34, 1)',
                                  borderRadius: '15px',
                                  color: '#fff',
                                  fontSize: '0.8em'
                                }}
                              />
                            )}
                            {hasRecommendedDiagnosis && question.recommendedSelection === false && (
                              <Tooltip
                                id={`recommendation-${panelIndex}-${questionIndex}-no`}
                                place="top"
                                style={{
                                  maxWidth: '300px',
                                  whiteSpace: 'normal',
                                  backgroundColor: 'rgba(34, 34, 34, 1)',
                                  borderRadius: '15px',
                                  color: '#fff',
                                  fontSize: '0.8em'
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

              </div>
            ))}

          {/* No Collateral Warning — same banner as the adult Decision for
              ADHD, with the wording changed from "in adults" to children and
              young people. Slides open when "None available" is ticked under
              Supporting Evidence (selections[5][6]); collapsed otherwise. */}
          <div className={`overflow-hidden transition-all duration-400 ease-in-out ${
              selections[5][6]?.includes('None available') ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <div className="bg-red-50 rounded-2xl p-4 text-left risk-banner">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <ExclamationIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-md font-medium text-red-800">For Consideration</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p className="font-medium">
                        In the absence of corroborating information, a diagnosis of Attention Deficit Hyperactivity Disorder in children and young people should be made with caution.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        
        ) : (
          // New component for non-ADHD diagnoses
          <NonADHDDiagnosticConfirmation canEdit={canEdit} onDirty={() => setHasChanges(true)} />
        )}

        {/* Supporting Documents, Formulation Notes, and Symptom Details - Hidden when in SSI Reset Mode.
            For deferred DF orgs, RDD is skipped so hasRecommendedDiagnosis is false — we show the
            panel via isDFDeferred to display formulation notes and internal case notes.
            TOC patients have already reviewed these on the dedicated MDT Review page, so both
            the Supporting Documents block and the Formulation Notes block are suppressed here. */}
        {shouldShowAdhdFlow && (hasRecommendedDiagnosis || wf.isDFDeferred) && !isSSIResetMode && !wf.hasMultiStepDD && (showCI || showSSI || showQbTest || showQbCheck || wf.isDFDeferred) && (
        <div className="w-full space-y-6 mt-6">
          {/* Supporting Documents + Formulation Notes row.
              For deferred-DF orgs the clinician's formulation notes (recorded
              on the SSI DF page) are shown read-only alongside the document
              links so the MDT panel can reference them during their review. */}
          <div className={`${wf.isDFDeferred ? 'flex gap-6' : ''}`}>
          <div className={`bg-gray-50 rounded-2xl border border-gray-200 p-6 ${wf.isDFDeferred ? 'w-1/2' : 'w-full'}`}>
            <h3 className="text-lg font-semibold mb-4">Supporting Documents</h3>
            <div className="flex gap-8">
              {/* Clinical Interview Report Preview */}
              {showCI && (
              <div className="flex flex-col items-center text-center">
                <div className="">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-800 mb-1">Clinical Interview</h4>
                  {isValidTimestamp(ciLastGenerated) && (
                    <p className="text-xs text-gray-500 mt-1">Last updated on {formatDateTime(ciLastGenerated)}</p>
                  )}
                </div>
                <button
                  onClick={() => handleOpenDocumentDialog("Clinical Interview")}
                  className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
                  onMouseEnter={() => { try { prefetchCI?.(); } catch {} }}
                  onFocus={() => { try { prefetchCI?.(); } catch {} }}
                >
                  <SearchIcon className="h-4 w-4" />
                  View
                </button>
              </div>
              )}

              {/* Semi-Structured Interview Report Preview */}
              {showSSI && (
              <div className="flex flex-col items-center text-center">
              <div className="">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-800 mb-1">Semi-Structured Interview</h4>
                  {isValidTimestamp(ssiLastGenerated) && (
                    <p className="text-xs text-gray-500 mt-1">Last updated on {formatDateTime(ssiLastGenerated)}</p>
                  )}
                </div>
                <button
                  onClick={() => handleOpenDocumentDialog("Semi-Structured Interview")}
                  className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
                  onMouseEnter={() => { try { prefetchSSI?.(); } catch {} }}
                  onFocus={() => { try { prefetchSSI?.(); } catch {} }}
                >
                  <SearchIcon className="h-4 w-4" />
                  View
                </button>
              </div>
              )}

              {showQbCheck && (
              <div className="flex flex-col items-center text-center">
                <div className="">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-800 mb-1">QbCheck Results</h4>
                  {isValidTimestamp(qbCheckLastGenerated) && (
                    <p className="text-xs text-gray-500 mt-1">Last updated on {formatDateTime(qbCheckLastGenerated)}</p>
                  )}
                </div>
                <button
                  onClick={() => handleOpenDocumentDialog("QbCheck")}
                  className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
                  onMouseEnter={() => { try { prefetchQbCheck?.(); } catch {} }}
                  onFocus={() => { try { prefetchQbCheck?.(); } catch {} }}
                >
                  <SearchIcon className="h-4 w-4" />
                  View
                </button>
              </div>
              )}

              {showQbTest && (
              <div className="flex flex-col items-center text-center">
                <div className="">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-800 mb-1">QbTest Results</h4>
                  {isValidTimestamp(qbTestLastGenerated) && (
                    <p className="text-xs text-gray-500 mt-1">Last updated on {formatDateTime(qbTestLastGenerated)}</p>
                  )}
                </div>
                <button
                  onClick={() => handleOpenDocumentDialog("QbTest")}
                  className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
                  onMouseEnter={() => { try { prefetchQbTest?.(); } catch {} }}
                  onFocus={() => { try { prefetchQbTest?.(); } catch {} }}
                >
                  <SearchIcon className="h-4 w-4" />
                  View
                </button>
              </div>
              )}
            </div>
          </div>

          {/* Formulation Notes + Internal Case Notes — only for deferred-DF orgs.
              Shows the assessing clinician's draft formulation notes and private
              case notes alongside Supporting Documents as a read-only MDT reference. */}
          {wf.isDFDeferred && (
            <div className="flex flex-col gap-4 w-1/2">
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 flex-1 flex flex-col">
                <h3 className="text-lg font-semibold mb-4">Emerging Formulation</h3>
                {formulationNotes ? (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap text-left bg-white rounded-lg p-4 border border-gray-200 flex-1">
                    {formulationNotes}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic text-left">
                    No formulation notes have been recorded.
                  </p>
                )}
              </div>
              {internalCaseNotes && (
                <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Confidential Case Notes</h3>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap text-left bg-white rounded-lg p-4 border border-gray-200 min-h-[80px]">
                    {internalCaseNotes}
                  </div>
                </div>
              )}
            </div>
          )}
          </div>

          {/* Symptom Details Panel */}
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Symptom Details</h3>
            <div>
              {keys.map((key, index) => {
                const adulthoodPresent = attentionDeficitData.concat(hyperactivityImpulsivityData).find(item => item.criterion === headers[index])?.adulthood;
                const childhoodPresent = attentionDeficitData.concat(hyperactivityImpulsivityData).find(item => item.criterion === headers[index])?.childhood;
                const symptomSettings = attentionDeficitData.concat(hyperactivityImpulsivityData).find(item => item.criterion === headers[index])?.presentInSettings_Adulthood || [];

                return (
                  <div key={key} className="mb-4 text-left">
                    <h4 className="px-4 font-semibold mb-2 pt-2 pb-4">{headers[index]}: {descriptions[headers[index]]}</h4>
                    
                    {/* CYP writes one approved paragraph per symptom, covering the
                        symptom as a whole. The two period columns are the
                        clinician's Yes/No decisions only. Adult ADHD's older
                        reports split the prose into adulthood and childhood;
                        a child assessment never does, so looking those keys
                        up shows "Not available" over a paragraph that exists. */}
                    <div className="flex">
                      <div className="px-4 w-1/4">
                        <div className="flex items-center pb-2">
                          {adulthoodPresent ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                          ) : (
                            <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                          )}
                          <h5 className="font-semibold">Last 6 months</h5>
                        </div>
                        {adulthoodPresent && symptomSettings.length > 0 && (
                          <p className="text-xs text-gray-600 mt-1">
                            Present in: {symptomSettings.join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="px-4 w-1/4">
                        <div className="flex items-center pb-2">
                          {childhoodPresent ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                          ) : (
                            <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                          )}
                          <h5 className="font-semibold">Before age 12</h5>
                        </div>
                      </div>
                      <div className="px-4 w-1/2">
                        <div className="flex items-center pb-2">
                          <h5 className="font-semibold">Summary of Symptom</h5>
                        </div>
                        <p className="text-sm">{outputs[`ssi_unified_${key}`] || 'Summary not available'}</p>
                      </div>
                    </div>
                    <div className="h-px bg-gray-300 m-4" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Document Embed Dialog */}
          <DocumentEmbedDialog
            isOpen={isDocumentDialogOpen}
            onClose={handleCloseDocumentDialog}
            reportStage={selectedReport}
            clinicianId={auth.currentUser?.uid}
            patientId={patientId}
            lastGenerated={selectedReport === 'Clinical Interview' 
              ? ciLastGenerated 
              : selectedReport === 'Semi-Structured Interview' 
                ? ssiLastGenerated 
                : selectedReport === 'QbCheck'
                  ? qbCheckLastGenerated
                  : selectedReport === 'QbTest'
                    ? qbTestLastGenerated
                    : null}
          />
        </div>
        )}


<div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">

<div className="w-1/3 flex justify-start">
  <button
    onClick={() => {
      const backRoute = wf.hasMultiStepDD
        ? `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/mdt-review`
        : `/patient/${patientId}`;
      // Existing dirty-aware back behaviour, gated behind the SSI decision
      // mismatch dialog first (no-op for non-mismatch orgs).
      const proceed = () => {
        if (hasChanges && canEdit && handleFinishDDRef.current) {
          setPendingExitRoute(backRoute);
          handleFinishDDRef.current();
          return;
        }
        flushPendingUpdates();
        navigate(backRoute);
      };
      if (attemptExit(proceed)) return;
      proceed();
    }}
    className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
  >
    <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
    <span className="font-bold">{wf.hasMultiStepDD ? ((wf.isMDTReview || wf.isTOCWorkflow) ? 'Back to MDT Review' : 'Back to Review') : 'Back to Patient Overview'}</span>
  </button>
</div>

<div className="w-1/3" />

<div className="w-1/3 flex justify-end">
{canEdit ? (
  <button
    onClick={() => { if (!attemptExit(handleFinishDDCore)) handleFinishDDCore(); }}
    className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${!isFormValid() || isSaving
      ? (isSaving ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
      : 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
      }`}
    disabled={!isFormValid() || isSaving}
  >
    <span className="font-bold">{isSaving ? "Saving Progress..." : (wf.isFlatDifferentials ? "Confirm ADHD Diagnostic Decision" : "Confirm Primary Diagnostic Decision")}</span>
    {isSaving
      ? <ButtonSpinner className="w-5 h-5 ml-2" />
      : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
  </button>
) : wf.hasMultiStepDD && (
  <button
    onClick={() => navigate(hasOtherConditions
      ? `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/other-conditions`
      : `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/final-formulation`
    )}
    className="flex items-center py-2 px-4 rounded-md shadow-md bg-violet-900 hover:bg-violet-800 text-white font-bold transition-all duration-300"
  >
    <span>{hasOtherConditions ? `Next: ${getFlatOtherConditionsStepTitle(wf.isTOCWorkflow)}` : 'Next: Formulation'}</span>
    <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
  </button>
)}
</div>
</div>

      </div>
      </div>{/* closes the flex items-start wrapper (TOC sidebar layout) */}
      {/* Module completion dialog for the primary Diagnostic Decision.
          Button ordering is MDT-aware: MDT Review orgs emphasise "Generate Report"
          as the primary CTA, while standard orgs emphasise "Go to PAD". */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) { setPendingExitRoute(null); pendingExitCallbackRef.current = null; }
        }}
        title={goesToMdtPlan
          ? "ADHD Diagnostic Decision"
          : shouldShowAdhdFlow ? "ADHD Diagnostic Decision Complete" : "Diagnostic Decision Complete"}
        description={goesToMdtPlan
          ? "Review your ADHD diagnostic decision below. The next step is to complete the MDT Plan."
          : shouldShowAdhdFlow ? "Review your ADHD diagnostic decision before proceeding." : "Review your diagnostic decision before proceeding."}
        actions={(() => {
          const canProceed = isDialogFormValid() && !isNavigating;
          const showBothOptions = shouldShowGenerateReportButton() && shouldShowPADButton();

          // Intercepted exit: the clinician was trying to leave via
          // breadcrumb, top nav, or sidebar. Show a single "Save &
          // Continue" button that triggers LLM regen and navigates to
          // wherever they were originally headed.
          if (pendingExitRoute) {
            return (
              <>
                <CancelButton onClick={() => { setPendingExitRoute(null); pendingExitCallbackRef.current = null; setIsDialogOpen(false); }} />
                <PrimaryActionButton
                  key="save-continue"
                  label={isNavigating ? "Saving..." : "Save & Continue"}
                  onClick={() => { if (!isNavigating) handleSaveAndContinue(); }}
                  disabled={!canProceed}
                />
              </>
            );
          }

          const generateReportBtn = shouldShowGenerateReportButton() && (
            <GenerateReportButton
              key="generate"
              onClick={() => { if (!isNavigating) handleGenerateReport(); }}
              disabled={!canProceed}
              secondary={showBothOptions && !wf.isMDTReview}
              savingLabel={isNavigating ? "Saving..." : undefined}
            />
          );

          const padBtn = shouldShowPADButton() && (
            <PrimaryActionButton
              key="pad"
              label={isNavigating ? "Saving..." : "Go to Post-Assessment Discussion"}
              onClick={() => { if (!isNavigating) handleGoToPostAssessmentDiscussion(); }}
              disabled={!canProceed}
              secondary={showBothOptions && wf.isMDTReview}
            />
          );

          const secondaryDiagnosesBtn = hasOtherConditions && (
            <PrimaryActionButton
              key="secondary"
              label={isNavigating ? "Saving..." : (wf.isFlatDifferentials ? `Go to ${getFlatOtherConditionsStepTitle(wf.isTOCWorkflow)}` : "Go to Secondary and/or Comorbid Diagnoses")}
              onClick={() => { if (!isNavigating) handleGoToSecondaryDiagnoses(); }}
              disabled={isNavigating}
            />
          );

          // TOC patients always proceed to Formulation (the final DD step)
          // rather than completing the module here.  When other conditions
          // exist, they go to Other Conditions first, then to Formulation.
          const formulationBtn = wf.hasMultiStepDD && !hasOtherConditions && (
            <PrimaryActionButton
              key="formulation"
              label={isNavigating ? "Saving..." : "Go to Formulation"}
              onClick={() => { if (!isNavigating) handleGoToFormulation(); }}
              disabled={!canProceed}
            />
          );

          // Semble MDT Report orgs (WLC): ADHD-positive patients proceed to
          // the MDT Plan page (the final DD step) rather than completing the
          // module here.  When other conditions exist, they go to Other
          // Conditions first, then on to the MDT Plan from there.
          const mdtPlanBtn = goesToMdtPlan && !hasOtherConditions && (
            <PrimaryActionButton
              key="mdt-plan"
              label={isNavigating ? "Saving..." : "Go to MDT Plan"}
              onClick={() => { if (!isNavigating) handleGoToMdtPlan(); }}
              disabled={!canProceed}
            />
          );

          return (
            <>
              <CancelButton onClick={() => setIsDialogOpen(false)} />
              {hasOtherConditions
                ? secondaryDiagnosesBtn
                : wf.hasMultiStepDD
                  ? formulationBtn
                  : goesToMdtPlan
                    ? mdtPlanBtn
                    : wf.isMDTReview
                      ? <>{padBtn}{generateReportBtn}</>
                      : <>{generateReportBtn}{padBtn}</>
              }
            </>
          );
        })()}
      >
        <SummarySection title={shouldShowAdhdFlow ? "Your ADHD Diagnostic Decision:" : "Your Diagnostic Decision:"}>
          {constructDiagnosisSummary()}
        </SummarySection>

        <DialogMessage>
          {(shouldShowAdhdFlow && selections[5][0] !== "Not met") || (!shouldShowAdhdFlow) || wf.hasMultiStepDD
            ? "Please review your selections. Are you happy to proceed?"
            : (
              <>
                Given your diagnostic decision for ADHD is that the criteria were 'Not met', please provide your recommended next steps before proceeding.
                {hasOtherConditions && (
                  <>
                    <br /><br />
                    <span className="font-bold">
                      You will also be able to record decisions and recommended next steps for other conditions that you have selected as under consideration on the next page.
                    </span>
                  </>
                )}
              </>
            )
          }
        </DialogMessage>

        {shouldShowAdhdFlow && selections[5][0] === "Not met" && !wf.hasMultiStepDD && (
          <div className="mb-6">
            <h4 className="font-bold mb-4 text-lg">My recommended next steps are:</h4>
            <div className="min-h-[10rem] self-stretch grow shrink basis-0 px-3.5 py-3 bg-white rounded-lg shadow border border-gray-300 flex-col justify-end items-start gap-2 flex">
              <textarea
                ref={textAreaRef}
                className={`self-stretch grow shrink basis-0 text-gray-900 text-base font-normal leading-normal ${!canEdit ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
                placeholder={canEdit ? "Type notes..." : "Notes can only be added by the assigned clinician"}
                rows="4"
                value={nextSteps}
                onChange={(e) => handleNotesChange(e.target.value)}
                onBlur={handleNotesBlur}
                disabled={!canEdit}
                aria-label="Your Recommended Next Steps"
              />
            </div>
          </div>
        )}
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="Diagnostic Decision"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={() => handleNextPageButtonClick(`/patient/${patientId}/cyp-adhd/documents`)}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />

      {/* Opens immediately when Fully / Partially / Not met does not line up
          with Semi-Structured Interview symptom / domain decisions. Two steps:
          ask whether to update the SSI, then (if yes) explain the takeover
          and choose whether to edit now or finish the Diagnostic Decision first. */}
      <SSIDdConsistencyDialog
        open={ssiDdConsistencyDialog.open}
        diagnosisDecision={ssiDdConsistencyDialog.diagnosisDecision}
        ssiClinicianName={
          patientDataModel?.clinicians?.clinicianSSI?.clinicianName ||
          'your colleague'
        }
        needsTakeover={needsSSITakeoverFromDD}
        isProcessing={isProcessingSSITakeover}
        onGoBackToDecision={handleGoBackToDecision}
        onKeepAsIs={handleKeepSSIAsIs}
        onConfirmEditNow={handleConfirmEditNow}
        onConfirmFinishDDFirst={handleConfirmFinishDDFirst}
      />

      {/* SSI Conflict Alert - 3 Borough / SLaM workflow only
          Shown when Lead clinician's final DD decision conflicts with SSI recommendation */}
      {isSSIFirstOrg && (
        <SSIConflictAlert
          isOpen={isConflictAlertOpen}
          onClose={handleConflictAlertClose}
          conflictDetails={conflictDetails}
          onViewSSIReport={handleViewSSIReport}
          onResetSSI={handleResetSSI}
          isResetting={isResettingSSI}
        />
      )}

      {/* Symptom / Domain Detail Dialog.
          Rendered at the Layout level (outside all conditional blocks) so it is
          always in the React tree.  Radix portals it to <body>, avoiding any CSS
          stacking context or overflow clipping from parent containers.
          Opens when the clinician clicks any row in the Symptoms or Domains tables.
          The status lines above are the period-by-period decisions. The
          paragraph underneath is the one approved SSI paragraph for that
          symptom or area as a whole — CYP does not write a separate
          paragraph for before and after age 12. */}
      <Dialog.Root
        open={Boolean(activeSymptomPopup)}
        onOpenChange={(open) => { if (!open) setActiveSymptomPopup(null); }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000]" />
          <Dialog.Content
            className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                       w-[90vw] max-w-2xl bg-white rounded-2xl shadow-2xl p-6
                       focus:outline-none z-[1001] text-left"
          >
            {activeSymptomPopup && (
              <>
                {/* Header: category label + name */}
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <Dialog.Title asChild>
                      <span className="text-xs font-bold uppercase tracking-widest text-primary">
                        {activeSymptomPopup.isDomain
                          ? 'Domain of Functional Impairment'
                          : `DSM-5 Criterion ${activeSymptomPopup.criterion}`}
                      </span>
                    </Dialog.Title>
                    <p className="text-base font-semibold text-gray-900 mt-1 leading-snug">
                      {activeSymptomPopup.symptom}
                    </p>
                  </div>
                  <Dialog.Close asChild>
                    <button
                      className="ml-4 text-gray-400 hover:text-gray-600 flex-shrink-0 rounded-full p-1 hover:bg-gray-100"
                      aria-label="Close"
                    >
                      <XIcon className="w-5 h-5" />
                    </button>
                  </Dialog.Close>
                </div>

                {/* Adulthood / Childhood status badges — stacked vertically so each stage
                    gets its own row and is easy to scan at a glance. */}
                <div className="flex flex-col gap-2 mb-5">
                  {/* Adulthood row (merged title when the onset question was
                      not asked: under-13 duration IS onset) */}
                  <div className="flex items-center gap-1.5">
                    {activeSymptomPopup.adulthoodPresent === 'inconclusive'
                      ? <ExclamationIcon className="w-4 h-4 text-amber-600" />
                      : activeSymptomPopup.adulthoodPresent
                        ? <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        : <XCircleIcon className="w-4 h-4 text-red-400" />}
                    <span className="text-sm font-medium text-gray-700">
                      {activeSymptomPopup.isDomain
                        ? (activeSymptomPopup.adulthoodPresent ? 'Impaired' : 'Not impaired')
                        : activeSymptomPopup.adulthoodPresent === 'inconclusive'
                          ? 'Inconclusive whether present'
                          : (activeSymptomPopup.adulthoodPresent ? 'Present' : 'Not present')
                      }
                      {mergeOnsetColumns && !activeSymptomPopup.isDomain
                        ? ' for 6 months or more and before age 12'
                        : ' in the last 6 months'}
                    </span>
                    {!activeSymptomPopup.isDomain && activeSymptomPopup.adulthoodPresent === true && activeSymptomPopup.settings?.length > 0 && (
                      <span className="text-xs text-gray-500 ml-1">
                        — Settings: {activeSymptomPopup.settings.join(', ')}
                      </span>
                    )}
                  </div>
                  {/* Childhood row */}
                  {!mergeOnsetColumns && (
                    <div className="flex items-center gap-1.5">
                      {activeSymptomPopup.childhoodPresent === 'inconclusive'
                        ? <ExclamationIcon className="w-4 h-4 text-amber-600" />
                        : activeSymptomPopup.childhoodPresent
                          ? <CheckCircleIcon className="w-4 h-4 text-green-500" />
                          : <XCircleIcon className="w-4 h-4 text-red-400" />}
                      <span className="text-sm font-medium text-gray-700">
                        {activeSymptomPopup.isDomain
                          ? (activeSymptomPopup.childhoodPresent ? 'Impaired' : 'Not impaired')
                          : activeSymptomPopup.childhoodPresent === 'inconclusive'
                            ? 'Inconclusive whether present'
                            : (activeSymptomPopup.childhoodPresent ? 'Present' : 'Not present')
                        } before age 12
                      </span>
                    </div>
                  )}
                </div>

                {/* The approved SSI paragraph for this symptom or area.
                    Adult ADHD's older reports stored two paragraphs
                    (adulthood and childhood). CYP stores one, under
                    ssi_unified_*, written for the symptom overall. The
                    period decisions are the status lines above, not a
                    second paragraph. */}
                {activeSymptomPopup.key && (
                  <div>
                    <p className="text-xs italic text-gray-400 mb-2">
                      {ssiReportApprovedByName && isValidTimestamp(ssiReportApprovedAt)
                        ? `Approved by ${ssiReportApprovedByName} on ${formatDateTime(ssiReportApprovedAt)}`
                        : ssiReportApprovedByName
                          ? `Approved by ${ssiReportApprovedByName}`
                          : 'Pending review'}
                    </p>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4 leading-relaxed">
                      {outputs[`ssi_unified_${activeSymptomPopup.key}`] || (
                        <span className="text-gray-400 italic">Summary not available.</span>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

    </Layout>
  );
}